import React, { useEffect, useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import {
  Form,
  Row,
  Col,
  Input,
  Checkbox,
  TimePicker,
  Typography,
  Modal,
  Select,
  TreeSelect,
  Button,
  Switch,
  Tooltip,
  Space,
  Descriptions,
  Tag,
  ConfigProvider,
  Popover,
} from 'antd';
import dayjs from 'dayjs';
import {
  add_operation_data_v1,
  get_actionlist_data,
  get_all_testing_type,
  get_calculation_field,
  get_cleaning_type_for_line,
  get_clearence_type,
  get_departments,
  // get_device_details,
  get_machine_types,
  get_material_inventory,
  get_operation_operation_type,
  get_operation_type,
  get_parameters,
  get_process_operation_by_recipe,
  get_process_with_operations,
  get_sensor_list,
  get_standard_blocks,
  get_units,
  get_sub_operation_by_operation_type,
  get_operation_data_v1,
  edit_operation_data_v1,
  get_process_data_for_process_id,
  get_register_machine,
  get_register_by_machine,
  get_total_ingredients_quantity,
} from '../mbmr/mbmr_api';
import {
  get_list_of_setting,
  get_machine_category_with_groups,
  get_roles,
  get_formula_definitions,
} from '../../../utils/api';
import {
  PackageSearch,
  ShieldCheck,
  ClipboardCheck,
  ListChecks,
  FileText,
  BookOpen,
  FlaskConical,
  Settings,
  Calculator,
  Cog,
  Network,
  Package,
  Plus,
  Pencil,
  Paperclip,
  X,
} from 'lucide-react';
import PageModal from '@/components/PageModal';
import Ingredients, { IngredientCard } from '../mbmr/Ingredients';
import Monitoring, { MonitoringCard } from '../mbmr/Monitoring';
import Product, { ProductCard } from '../mbmr/Product';
import ActionList, { ActionListCard } from '../mbmr/ActionList';
import Checklist, { ChecklistCard } from '../mbmr/Checklist';
import Instructions, { InstructionsCard } from '../mbmr/Instructions';
import SamplingForm, { SamplingCard } from '../mbmr/Sampling';
import LineClearanceForm, { LineClearanceCard } from '../mbmr/LineClearance';
import ApiCalculationForm, { ApiCalculationCard } from '../mbmr/APICalculation';
import MachineSetup, { MachineSetupCard } from '../mbmr/MachineSetup';
import { useParams } from 'react-router';
import MbomPanel from '@/components/MbomPanel';
// RDY-FE: readiness submit control + the post-save single-operation readiness refresh.
import ReadinessSubmitButton from './ReadinessSubmitButton';
import ReturnForCorrectionButton from './ReturnForCorrectionButton';
import { useReadiness } from './ReadinessContext';
import SopLinks from '@/components/SopLinks';
import { Drawer as MbomDrawer } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { toggleRefresh } from '@/store/reducers/recipeConfigRefresh';
import { setTotalQty } from '@/store/reducers/ingredientQty';
import { toast } from 'react-toastify';
import { defaultColors } from '@/themes/antd-theme';
import { useTheme } from '@/App';
import { useAppSettingValue, useSubOperationAlias } from '@/hooks/useAppSetting';

const { Title } = Typography;

// Normalize a sub-operation label for loose matching.
// Strips spaces, hyphens, underscores and lowercases so that
// "Api Calculations" == "API Calculation" == "api_calculation" etc.
// Trailing 's' stripped for plural tolerance (e.g. "Calculations" == "Calculation").
const normalizeSubOpLabel = (str = '') =>
  str.toLowerCase().replace(/[\s_-]+/g, '').replace(/s$/, '');

const subOpLabelMatches = (a = '', b = '') =>
  normalizeSubOpLabel(a) === normalizeSubOpLabel(b);

const validateArrayFields = (formValues) => {
  const {
    ingredients,
    monitoring,
    products,
    action_list,
    checklist,
    instructions,
    sampling,
    line_clearance,
    api_calculation,
    machines,
  } = formValues;

  const arraysToCheck = [
    ingredients,
    monitoring,
    products,
    action_list,
    checklist,
    instructions,
    sampling,
    line_clearance,
    api_calculation,
    machines,
  ];
  // Check At least one array should contain data
  return arraysToCheck.some((arr) => Array.isArray(arr) && arr.length > 0);
};

// Deviation rule options (shared with Monitoring/Ingredients) — when a value is
// treated as a deviation.
const DEVIATION_RULE_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'less_than_min', label: 'Less than Min' },
  { value: 'greater_than_max', label: 'Greater than Max' },
  { value: 'less_than_min_or_greater_than_max', label: 'Less than Min or Greater than Max' },
];

/** Visible chars for operation title in header bar (full text on hover). */
const OPERATION_TITLE_DISPLAY_MAX = 64;

const sub_operations = [
  {
    id: 'ingredients',
    label: 'Ingredients',
    icon: PackageSearch,
    component: Ingredients,
    view_component: IngredientCard,
  },
  {
    id: 'monitoring',
    label: 'Monitoring',
    icon: ShieldCheck,
    component: Monitoring,
    view_component: MonitoringCard,
  },
  {
    id: 'products',
    label: 'Product',
    icon: ClipboardCheck,
    component: Product,
    view_component: ProductCard,
  },
  {
    id: 'action_list',
    label: 'Action List',
    icon: ListChecks,
    component: ActionList,
    view_component: ActionListCard,
  },
  {
    id: 'checklist',
    label: 'Checklist',
    icon: FileText,
    component: Checklist,
    view_component: ChecklistCard,
  },
  {
    id: 'instructions',
    label: 'Instruction',
    icon: BookOpen,
    component: Instructions,
    view_component: InstructionsCard,
  },
  {
    id: 'sampling',
    label: 'Sampling',
    icon: FlaskConical,
    component: SamplingForm,
    view_component: SamplingCard,
  },
  {
    id: 'line_clearance',
    label: 'Line Clearance',
    icon: Settings,
    component: LineClearanceForm,
    view_component: LineClearanceCard,
  },
  {
    id: 'api_calculation',
    label: 'Api Calculations',
    icon: Calculator,
    component: ApiCalculationForm,
    view_component: ApiCalculationCard,
  },
  {
    id: 'machines',
    label: 'Machine Setup',
    icon: Cog,
    component: MachineSetup,
    view_component: MachineSetupCard,
  },
];

// ==============================|| FILE ATTACHMENT CONFIG MODAL ||============================== //

const FileAttachmentConfigModal = ({ open, onClose, onSave, title, initialData, readOnly, zIndex }) => {
  const { isDark } = useTheme();
  const textColor = isDark ? '#f1f5f9' : '#1e293b';
  const subTextColor = isDark ? defaultColors.palette.gray[400] : '#64748b';
  const borderColor = isDark ? defaultColors.palette.gray[600] : '#e2e8f0';
  const rowBg = isDark ? defaultColors.palette.gray[750] || '#374151' : '#ffffff';
  const bodyBg = isDark ? defaultColors.palette.gray[800] : '#f8fafc';
  const inputBg = isDark ? defaultColors.palette.gray[700] : '#ffffff';

  const FILE_SIZE_OPTIONS = [
    { label: '200 KB – 500 KB',   value: '200-500KB'   },
    { label: '500 KB – 800 KB',   value: '500-800KB'   },
    { label: '800 KB – 1 MB',     value: '800KB-1MB'   },
    { label: '1 MB – 1.5 MB',     value: '1-1.5MB'     },
    { label: '1.5 MB – 2 MB',     value: '1.5-2MB'     },
    { label: '≤ 2 MB',            value: '<=2MB'       },
  ];

  const makeEmptyRow = () => ({ label: '', file_types: [], image_types: [], max_size: '' });
  const [rows, setRows] = useState([makeEmptyRow()]);
  const [imagePopoverOpen, setImagePopoverOpen] = useState({});

  useEffect(() => {
    if (open) {
      setRows(
        Array.isArray(initialData) && initialData.length > 0
          ? initialData.map((r) => ({ ...r }))
          : [makeEmptyRow()]
      );
      setImagePopoverOpen({});
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateRow = (idx, field, value) => {
    setRows((prev) =>
      prev.map((r, i) => {
        if (i !== idx) return r;
        const updated = { ...r, [field]: value };
        if (field === 'file_types') {
          if (!value.includes('Image')) {
            updated.image_types = [];
          } else if (!r.file_types.includes('Image')) {
            // Image newly added — default to JPG
            updated.image_types = ['JPG'];
          }
        }
        return updated;
      })
    );
  };

  const handleSave = () => {
    // Every row configured here is, by definition, a required upload (the section is
    // titled "Required File Uploads" and there is no optional-file concept in this UI).
    // The execution-side gate (djangoapp/services/execution/attachments.py::step_requirements)
    // only treats a row as a real requirement when it carries `required: true` — without
    // this flag the row saves fine but is silently never enforced or shown as a blocker.
    const valid = rows
      .filter((r) => r.label.trim() && r.file_types.length > 0)
      .map((r) => ({ ...r, required: true }));
    onSave(valid);
    onClose();
  };

  return (
    <PageModal
      open={open}
      icon={<Paperclip size={22} />}
      title={title || 'Configure Required Files'}
      description="Define what operators must upload for this section"
      onCancel={onClose}
      width={860}
      zIndex={zIndex || 1500}
      bodyStyle={{ backgroundColor: bodyBg, padding: '16px 24px 8px' }}
      footer={
        <div className="flex justify-end gap-2">
          {!readOnly && <Button onClick={onClose}>Cancel</Button>}
          <Button type="primary" onClick={readOnly ? onClose : handleSave}>{readOnly ? 'Close' : 'Save Configuration'}</Button>
        </div>
      }
    >
      {/* Column headers */}
      {rows.length > 0 && (
        <div
          className="flex items-center gap-2 px-3 pb-2 mb-1 text-xs font-semibold uppercase tracking-wide"
          style={{ color: subTextColor, borderBottom: `1px solid ${borderColor}` }}
        >
          <span style={{ flex: '2 1 0', minWidth: 0 }}>Label</span>
          <span style={{ flex: '1.4 1 0', minWidth: 150 }}>File Types</span>
          <span style={{ flex: '0 0 130px' }}>Max Size</span>
          {!readOnly && <span style={{ flex: '0 0 56px' }}></span>}
        </div>
      )}

      <div className="flex flex-col gap-2 py-2">
        {rows.length === 0 && readOnly && (
          <div
            className="flex flex-col items-center justify-center py-10 rounded-xl"
            style={{ border: `2px dashed ${borderColor}`, color: subTextColor }}
          >
            <Paperclip size={28} style={{ opacity: 0.3, marginBottom: 8 }} />
            <div className="text-sm font-medium">No file requirements configured</div>
            <div className="text-xs mt-1" style={{ opacity: 0.7 }}>No uploads are required for this section</div>
          </div>
        )}
        {rows.map((row, idx) => (
          <div
            key={idx}
            className="flex items-center gap-2 px-3 py-2.5 rounded-xl transition-shadow"
            style={{
              backgroundColor: rowBg,
              border: `1px solid ${borderColor}`,
              boxShadow: isDark ? 'none' : '0 1px 3px rgba(0,0,0,0.06)',
            }}
          >
            {/* Row number badge */}
            <div
              className="flex-shrink-0 flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold"
              style={{
                background: 'linear-gradient(135deg, #eff6ff 0%, #e0e7ff 100%)',
                color: '#3b82f6',
                border: '1px solid #bfdbfe',
                fontSize: 10,
              }}
            >
              {idx + 1}
            </div>
            {/* Label */}
            <input
              className="rounded-lg border px-2.5 py-1.5 text-sm outline-none transition-colors"
              style={{
                flex: '2 1 0',
                minWidth: 0,
                backgroundColor: inputBg,
                borderColor: borderColor,
                color: textColor,
              }}
              placeholder="e.g. Upload Batch Certificate"
              value={row.label}
              disabled={readOnly}
              onChange={(e) => updateRow(idx, 'label', e.target.value)}
            />
            {/* File types — Image tag opens a small popover for format selection */}
            <Select
              mode="multiple"
              placeholder="Select types"
              value={row.file_types}
              disabled={readOnly}
              onChange={(val) => updateRow(idx, 'file_types', val)}
              style={{ flex: '1.4 1 0', minWidth: 150 }}
              size="middle"
              options={[
                { label: 'PDF', value: 'PDF' },
                { label: 'Excel', value: 'Excel' },
                { label: 'Image', value: 'Image' },
              ]}
              tagRender={(tagProps) => {
                const { label: tagLabel, value: tagValue, closable, onClose } = tagProps;
                /* ── Image tag → popover for format selection ── */
                if (tagValue === 'Image') {
                  const formats = row.image_types || [];
                  const isOpen = !!imagePopoverOpen[idx];
                  return (
                    <Popover
                      trigger="click"
                      open={isOpen}
                      onOpenChange={(v) => setImagePopoverOpen((prev) => ({ ...prev, [idx]: v }))}
                      placement="bottom"
                      title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {/* camera icon */}
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                            <circle cx="12" cy="13" r="4"/>
                          </svg>
                          <span style={{ fontSize: 12, fontWeight: 600 }}>Image Formats</span>
                        </div>
                      }
                      content={
                        readOnly ? (
                          <span style={{ fontSize: 12, color: subTextColor }}>
                            {formats.length > 0 ? formats.join(', ') : 'No formats selected'}
                          </span>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 2px', minWidth: 140 }}>
                            {[
                              { fmt: 'JPG',  emoji: '🖼️' },
                              { fmt: 'PNG',  emoji: '🖼️' },
                              { fmt: 'JPEG', emoji: '📷' },
                            ].map(({ fmt, emoji }) => (
                              <Checkbox
                                key={fmt}
                                checked={formats.includes(fmt)}
                                onChange={(e) => {
                                  const next = e.target.checked
                                    ? [...formats, fmt]
                                    : formats.filter((f) => f !== fmt);
                                  updateRow(idx, 'image_types', next);
                                }}
                              >
                                <span style={{ fontSize: 13 }}>{emoji} {fmt}</span>
                              </Checkbox>
                            ))}
                          </div>
                        )
                      }
                    >
                      <Tag
                        closable={!readOnly && closable}
                        onClose={(e) => { e.preventDefault(); onClose(); }}
                        style={{
                          cursor: 'pointer',
                          background: formats.length > 0 ? '#dbeafe' : '#eff6ff',
                          borderColor: formats.length > 0 ? '#3b82f6' : '#bfdbfe',
                          color: '#1d4ed8',
                          userSelect: 'none',
                          marginRight: 3,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setImagePopoverOpen((prev) => ({ ...prev, [idx]: !isOpen }));
                        }}
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                          <circle cx="12" cy="13" r="4"/>
                        </svg>
                        {formats.length > 0 ? `Image (${formats.join(', ')})` : 'Image ▾'}
                      </Tag>
                    </Popover>
                  );
                }
                /* ── PDF tag ── */
                if (tagValue === 'PDF') {
                  return (
                    <Tag closable={!readOnly && closable} onClose={onClose}
                      style={{ marginRight: 3, background: '#fef2f2', borderColor: '#fca5a5', color: '#b91c1c', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                      </svg>
                      PDF
                    </Tag>
                  );
                }
                /* ── Excel tag ── */
                if (tagValue === 'Excel') {
                  return (
                    <Tag closable={!readOnly && closable} onClose={onClose}
                      style={{ marginRight: 3, background: '#f0fdf4', borderColor: '#86efac', color: '#166534', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                        <line x1="8" y1="13" x2="16" y2="13"/><line x1="8" y1="17" x2="16" y2="17"/><polyline points="10 9 9 9 8 9"/>
                      </svg>
                      Excel
                    </Tag>
                  );
                }
                return (
                  <Tag closable={!readOnly && closable} onClose={onClose} style={{ marginRight: 3 }}>
                    {tagLabel}
                  </Tag>
                );
              }}
            />
            {/* Max file size */}
            <Select
              placeholder="Size range"
              value={row.max_size || undefined}
              disabled={readOnly}
              onChange={(val) => updateRow(idx, 'max_size', val)}
              style={{ flex: '0 0 130px' }}
              size="middle"
              options={FILE_SIZE_OPTIONS}
            />
            {/* Actions */}
            {!readOnly && (
              <div className="flex items-center gap-1 flex-shrink-0" style={{ flex: '0 0 56px' }}>
                <Tooltip title="Remove row">
                  <button
                    type="button"
                    onClick={() => setRows((prev) => prev.filter((_, i) => i !== idx))}
                    className="flex items-center justify-center w-6 h-6 rounded-lg transition-colors"
                    style={{
                      color: rows.length === 1 ? '#d1d5db' : '#ef4444',
                      backgroundColor: rows.length === 1 ? 'transparent' : '#fef2f2',
                      cursor: rows.length === 1 ? 'not-allowed' : 'pointer',
                    }}
                    disabled={rows.length === 1}
                  >
                    <X size={13} />
                  </button>
                </Tooltip>
                {idx === rows.length - 1 && (
                  <Tooltip title="Add another file requirement">
                    <button
                      type="button"
                      onClick={() => setRows((prev) => [...prev, makeEmptyRow()])}
                      className="flex items-center justify-center w-6 h-6 rounded-lg transition-colors"
                      style={{
                        color: '#3b82f6',
                        backgroundColor: '#eff6ff',
                        border: '1px solid #bfdbfe',
                      }}
                    >
                      <Plus size={13} />
                    </button>
                  </Tooltip>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Footer hint */}
      {!readOnly && (
        <div className="flex items-center gap-1.5 pt-1 pb-2" style={{ color: subTextColor, fontSize: 11 }}>
          <span style={{ opacity: 0.6 }}>Rows without a label or file type will be discarded on save.</span>
        </div>
      )}
    </PageModal>
  );
};

// ==============================|| INLINE FILE CONFIG ||============================== //

const InlineFileConfig = ({ value, onChange, readOnly }) => {
  const { isDark } = useTheme();
  const textColor = isDark ? '#f1f5f9' : '#1e293b';
  const subTextColor = isDark ? defaultColors.palette.gray[400] : '#64748b';
  const borderColor = isDark ? defaultColors.palette.gray[600] : '#e2e8f0';
  const rowBg = isDark ? defaultColors.palette.gray[750] || '#374151' : '#ffffff';
  const sectionBg = isDark ? 'rgba(99,102,241,0.06)' : '#f8faff';
  const inputBg = isDark ? defaultColors.palette.gray[700] : '#ffffff';

  const FILE_SIZE_OPTIONS = [
    { label: '200 KB – 500 KB', value: '200-500KB' },
    { label: '500 KB – 800 KB', value: '500-800KB' },
    { label: '800 KB – 1 MB',   value: '800KB-1MB' },
    { label: '1 MB – 1.5 MB',   value: '1-1.5MB'   },
    { label: '1.5 MB – 2 MB',   value: '1.5-2MB'   },
    { label: '≤ 2 MB',          value: '<=2MB'      },
  ];

  const rows = Array.isArray(value) ? value : [];
  const makeEmptyRow = () => ({ label: '', file_types: [], image_types: [], max_size: '' });

  const addRow = () => onChange([...rows, makeEmptyRow()]);
  const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));
  const updateRow = (idx, field, val) => {
    onChange(rows.map((r, i) => {
      if (i !== idx) return r;
      const updated = { ...r, [field]: val };
      if (field === 'file_types' && !val.includes('Image')) updated.image_types = [];
      return updated;
    }));
  };

  return (
    <div style={{ borderTop: `1px solid ${borderColor}`, background: sectionBg, padding: '10px 16px 12px' }}>
      <div className="flex items-center gap-2 mb-2">
        <FileText size={14} style={{ color: '#6366f1', flexShrink: 0 }} />
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: textColor }}>
          Required File Uploads
        </span>
        {!readOnly && (
          <button
            type="button"
            onClick={addRow}
            className="ml-auto flex items-center gap-1 rounded-full"
            style={{ fontSize: 11, fontWeight: 600, color: '#6366f1', background: isDark ? 'rgba(99,102,241,0.15)' : '#eff0fe', border: '1px solid #a5b4fc', padding: '2px 10px', cursor: 'pointer' }}
          >
            <Plus size={11} />
            Add Requirement
          </button>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="flex items-center gap-2" style={{ fontSize: 12, color: subTextColor, opacity: 0.65, padding: '4px 0' }}>
          <FileText size={13} style={{ opacity: 0.4 }} />
          {readOnly ? 'No file uploads required for this section.' : 'No requirements yet — click Add Requirement to define uploads operators must provide.'}
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          {rows.map((row, idx) => (
            <div
              key={idx}
              className="flex items-center gap-2 rounded-lg"
              style={{ background: rowBg, border: `1px solid ${borderColor}`, padding: '6px 10px', boxShadow: isDark ? 'none' : '0 1px 2px rgba(0,0,0,0.04)' }}
            >
              <div
                className="flex-shrink-0 flex items-center justify-center rounded-full"
                style={{ width: 20, height: 20, background: 'linear-gradient(135deg,#eff6ff,#e0e7ff)', color: '#6366f1', border: '1px solid #c7d2fe', fontSize: 10, fontWeight: 700 }}
              >
                {idx + 1}
              </div>
              <input
                className="rounded-md border outline-none"
                style={{ flex: '2 1 0', minWidth: 0, padding: '4px 8px', fontSize: 13, backgroundColor: inputBg, borderColor, color: textColor }}
                placeholder="e.g. Upload Batch Certificate"
                value={row.label}
                disabled={readOnly}
                onChange={(e) => updateRow(idx, 'label', e.target.value)}
              />
              <Select
                mode="multiple"
                placeholder="File types"
                value={row.file_types}
                disabled={readOnly}
                onChange={(val) => updateRow(idx, 'file_types', val)}
                style={{ flex: '1.4 1 0', minWidth: 140 }}
                size="small"
                options={[
                  { label: 'PDF', value: 'PDF' },
                  { label: 'Excel', value: 'Excel' },
                  { label: 'Image', value: 'Image' },
                ]}
              />
              {row.file_types.includes('Image') ? (
                <Select
                  mode="multiple"
                  placeholder="Image formats"
                  value={row.image_types}
                  disabled={readOnly}
                  onChange={(val) => updateRow(idx, 'image_types', val)}
                  style={{ flex: '1.1 1 0', minWidth: 120 }}
                  size="small"
                  options={[
                    { label: 'JPG', value: 'JPG' },
                    { label: 'PNG', value: 'PNG' },
                    { label: 'JPEG', value: 'JPEG' },
                  ]}
                />
              ) : (
                <div style={{ flex: '1.1 1 0', minWidth: 120 }} />
              )}
              <Select
                placeholder="Max size"
                value={row.max_size || undefined}
                disabled={readOnly}
                onChange={(val) => updateRow(idx, 'max_size', val)}
                style={{ flex: '0 0 130px' }}
                size="small"
                options={FILE_SIZE_OPTIONS}
              />
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => removeRow(idx)}
                  className="flex-shrink-0 flex items-center justify-center rounded-md"
                  style={{ width: 24, height: 24, color: '#ef4444', background: '#fef2f2', border: '1px solid #fecaca', cursor: 'pointer' }}
                >
                  <X size={12} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const OperationForm = ({
  onSubmit = [],
  from = 'config',
  isShowDag,
  set_isShowDag,
  handleBlockSubmit,
  blockData,
  is_add_middle_modal,
  set_is_add_middle_modal,
  set_isAddOperation,
  isAddOperation,
  copyFromOperationId,
  fromView,
  processApprovalStatus = true,
}) => {
  const { isDark } = useTheme();
  const params = useParams();
  // For a recipe-less library process the URL is /process-config/library/:libId,
  // so the recipe param is the literal "library". Treat it (and any non-numeric
  // value) as "no recipe" so the recipe-scoped fetch guards short-circuit and we
  // never call recipe APIs with recipe_id=library.
  const recipe_id = params?.recipe_id && !isNaN(Number(params.recipe_id)) ? params.recipe_id : null;
  // RDY-FE: null outside the editor shell (this component is also mounted standalone).
  const readiness = useReadiness();
  // Alias for the "Instruction" label, configurable in Application Settings
  const instructionAlias = useAppSettingValue('Instruction Label Alias');
  const instructionLabel = (instructionAlias && instructionAlias.trim()) || 'Instruction';
  // Sub-operation tab-label aliases (Ingredients, Monitoring, …), configurable in
  // Application Settings → "Sub-operation Alias". Falls back to the original label.
  const subOpAlias = useSubOperationAlias();
  const processConfig = useSelector((state) => state.processConfig);
  const dispatch = useDispatch();

  // true when editing is not allowed (view-only mode OR process approval pending)
  const isLocked = fromView || !processApprovalStatus;
  // Standard Block library form + PR Reject temporary-operation form share the same
  // OperationForm UX (no processConfig / MBMR add-op chrome).
  const isBlockLikeForm = from === 'standard_block' || from === 'temporary_operation';

  // Dark mode aware colors
  const cardBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const formBgColor = isDark ? defaultColors.palette.gray[800] : '#ffffff';
  const textColor = isDark ? '#ffffff' : '#374151';
  const secondaryTextColor = isDark ? defaultColors.palette.gray[400] : '#6b7280';
  const tabBgColor = isDark ? defaultColors.palette.gray[700] : '#e5e7eb';
  const activeTabBgColor = isDark ? defaultColors.palette.gray[600] : '#ffffff';
  const borderColor = isDark ? defaultColors.palette.gray[600] : '#e5e7eb';
  const {
    remainingQty, unit: remainingUnit,
    configuredIngredientId, configuredIngredientName, configuredIngredientRemaining,
  } = useSelector((state) => state.ingredientQty);

  // Authoritative remaining from the backend: master − Σ(all ingredients across ALL
  // operations). Call after every ingredient/operation save so the header settles to
  // the true value (no client-side delta accumulation drift).
  // --- Single live counter for the configured ingredient remaining ---
  // committedConfigured: remaining after rows already SAVED this session (seeded from the
  //   backend value, adjusted on each ingredient Save by its delta).
  // liveConfigured: what the header + form show — committedConfigured minus the modal's
  //   current (unsaved) entry. Recomputed per keystroke, so Cancel needs no special undo.
  const [committedConfigured, setCommittedConfigured] = useState(null);
  const [liveConfigured, setLiveConfigured] = useState(null);
  // Same single-counter model for the MASTER batch remaining (non-input-based recipes).
  // committedMaster: master remaining after rows already saved this session.
  // liveMaster: header value — committedMaster minus the modal's current entry.
  const [committedMaster, setCommittedMaster] = useState(null);
  const [liveMaster, setLiveMaster] = useState(null);

  const fetchRemainingQty = React.useCallback(() => {
    if (!recipe_id) return;
    get_total_ingredients_quantity(
      (res) => {
        if (res?.data?.status === 'success' && res?.data?.data) {
          const d = res.data.data;
          const totalQty =
            d.remaining_quantity ?? d.total_quantity ?? d.quantity ?? d.final_quantity ?? null;
          const unit = d.unit || d.unit_name || '';
          dispatch(setTotalQty({ totalQty, unit, existingSum: 0, inputIngredient: d.input_ingredient || null }));
          // Hard-reset the live counters from the fresh authoritative value. Done directly
          // (not only via the Redux-derived effect) so clicking Add/Update Operation always
          // resets the remaining, even if the numeric value is unchanged.
          const freshRemaining = d.input_ingredient?.remaining_quantity ?? null;
          setCommittedConfigured(freshRemaining);
          setLiveConfigured(freshRemaining);
          const freshMaster = totalQty;
          setCommittedMaster(freshMaster);
          setLiveMaster(freshMaster);
        }
      },
      { recipe_id }
    );
  }, [recipe_id, dispatch]);

  useEffect(() => {
    fetchRemainingQty();
  }, [fetchRemainingQty]);

  // When the selected operation OR the selected process changes on this page
  // (select / deselect / switch), re-pull the actual remaining from the API so
  // the counter reflects the true value for what's now in view (and isn't left
  // stale from whatever operation/process was previously selected — the master
  // remaining is recipe-wide, so any operation saved anywhere under this recipe
  // can change it).
  useEffect(() => {
    fetchRemainingQty();
  }, [processConfig?.operation_id, processConfig?.process_id, fetchRemainingQty]);

  // Also seed from the Redux-derived values when they first arrive (initial load).
  useEffect(() => {
    if (committedConfigured === null && configuredIngredientRemaining !== null) {
      setCommittedConfigured(configuredIngredientRemaining);
      setLiveConfigured(configuredIngredientRemaining);
    }
  }, [configuredIngredientRemaining, committedConfigured]);

  useEffect(() => {
    if (committedMaster === null && remainingQty !== null) {
      setCommittedMaster(remainingQty);
      setLiveMaster(remainingQty);
    }
  }, [remainingQty, committedMaster]);

  const [data, set_data] = useState([]);
  const [edit_data, set_edit_data] = useState([]);
  const refreshpage = useSelector((state) => state?.recipeconfigrefresh?.refresh);

  // Recipe edits (e.g. Master Batch Size change in EditRecipeModal) toggle
  // refreshpage on success — re-pull the authoritative remaining so the header
  // reflects the new master size without needing a hard refresh.
  useEffect(() => {
    fetchRemainingQty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshpage]);

  let operation_list = [];
  if (data.length > 0) {
    ({ operation_list } = data[0]);
  }
  const headerContent = useSelector((state) => state.headerContent);
  const selectedModule = useSelector((state) => state.moduleView);
  const [processData, setProcessData] = useState({});
  const success_get_operation_operation_type = (res) => {
    if (res.data.status === 'success') {
      const data = res?.data?.data;
      set_data(data);
      if (data?.length > 0) {
        const op_list = data[0]?.operation_list;
        // setFormValues({
        //   operation_number: op_list?.length + 1,
        //   dependencies: "",
        //   operation_name: "",
        //   execution_timeline: false,
        //   operation_type_id: null,
        //   department_id: null,
        //   mass: true,
        //   min_duration: "00:05",
        //   max_duration: "00:05",
        //   ingredients: [],
        //   monitoring: [],
        //   products: [],
        //   action_list: [],
        //   checklist: [],
        //   instructions: [],
        //   sampling: [],
        //   line_clearance: [],
        //   api_calculation: [],
        //   machines: [],
        // });
      }
    }
  };
  const validationSchema = Yup.object({
    ...(from === 'config' && {
      operation_number: Yup.number().required('Required'),
    }),
    operation_type_id: Yup.number()
      .nullable() // allow null
      .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value))
      .required('Please select operation type'),
    operation_name: Yup.string().required('Please enter operation name'),
    min_duration: Yup.string().when('is_duration_required', {
      is: true,
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.nullable(),
    }),
    department_id: Yup.number()
      .nullable()
      .transform((value, originalValue) => (String(originalValue).trim() === '' ? null : value))
      .required('Please select department'),
    max_duration: Yup.string().when('is_duration_required', {
      is: true,
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.nullable(),
    }),
  });

  // // function for trimming processes and operations based on operation_number
  // for dependancy select while add operation in between
  function trimProcessesByOperationNumber(processes, maxNumber) {
    if (!Array.isArray(processes)) return [];
    return processes
      .map((process) => ({
        ...process,
        operation: Array.isArray(process.operation)
          ? process.operation.filter((op) => op.operation_number <= maxNumber)
          : [],
      }))
      .filter((process) => process.operation.length > 0);
  }

  const WorkFlowButton = () => {
    return (
      <button
        className={`flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-full shadow-lg text-white ${isShowDag
            ? 'bg-green-800'
            : isDark
              ? 'bg-[#0F172A] hover:scale-110'
              : 'bg-[#204448] hover:scale-110'
          }`}
        // style={{
        //   transform: isShowDag ? "scale(1.05)" : "scale(1)",
        //   transition: "transform 0.15s ease-in-out",
        // }}
        onClick={() => set_isShowDag(!isShowDag)}
      >
        <Network size={20} />
      </button>
    );
  };

  const [mbomOpen, setMbomOpen] = useState(false);
  const MbomButton = () => (
    <>
      <button
        type="button"
        onClick={() => setMbomOpen(true)}
        title="Master Bill of Materials — the MFR evaluated at this batch size"
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border bg-transparent
           ${isDark ? 'border-[#5FC0CC] text-[#5FC0CC]' : 'border-[#204448] text-[#204448]'}
           shadow-sm transform-gpu hover:scale-110 transition-transform duration-200`}
      >
        MBOM
      </button>
      <MbomDrawer open={mbomOpen} onClose={() => setMbomOpen(false)} width={1000} title="Master Bill of Materials">
        {recipe_id ? <MbomPanel recipeId={recipe_id} /> : <span>MBOM is available on a recipe, not on a library process.</span>}
      </MbomDrawer>
    </>
  );

  const success_get_edit_operation_details = (res) => {
    if (res.data.status === 'success') {
      const editData = res?.data?.data;
      set_edit_data(res?.data?.data);
      // to get sub op list based on selected operation type
      if (editData?.operation_type_id) {
        get_sub_operation_by_operation_type(success_get_subopList_byOpType, {
          operation_type_id: editData?.operation_type_id,
        }); // to get sub op list based on selected operation type
      }

      if (editData) {
        // Derive the hold-time gate from the operation's type, so the Hold Min/Max
        // fields show on edit when that operation type requires hold time.
        const editOpType = (operation_type_list || []).find(
          (item) => item.operation_type_id === editData?.operation_type_id
        );
        setFormValues((prevdata) => ({
          ...prevdata, // keep existing state
          ...editData, // overwrite with API values
          is_hold_time: !!editOpType?.is_hold_time,
          // Backend stores hold time as HH:MM:SS; the TimePicker uses HH:MM.
          hold_min_duration: editData?.hold_min_time
            ? String(editData.hold_min_time).slice(0, 5)
            : null,
          hold_max_duration: editData?.hold_max_time
            ? String(editData.hold_max_time).slice(0, 5)
            : null,
        }));
      }

      // Seed the machine-group selections already saved for this operation's
      // dependencies, so re-editing can disable groups that are already used
      // (per upstream operation). Backend returns:
      //   dependent_machine_groups: [{operation_number, selected_machine_group:[ids]}]
      const savedDepGroups = editData?.dependent_machine_groups;
      if (Array.isArray(savedDepGroups)) {
        const seeded = {};
        savedDepGroups.forEach((entry) => {
          const opNum = entry?.operation_number;
          const ids = Array.isArray(entry?.selected_machine_group)
            ? entry.selected_machine_group
            : [];
          if (opNum != null && ids.length > 0) seeded[String(opNum)] = ids;
        });
        setSelectedMachineGroups(seeded);
      }
    }
  };

  useEffect(() => {
    if (processConfig?.operation_id) {
      const timeout = setTimeout(() => {
        get_operation_data_v1(success_get_edit_operation_details, {
          operation_id: processConfig?.operation_id,
        });
      }, 50);
      return () => clearTimeout(timeout);
    } else if (isBlockLikeForm && blockData) {
      setFormValues((prev) => ({
        ...prev,
        ...blockData,
      }));
      if (blockData?.operation_type_id) {
        get_sub_operation_by_operation_type(success_get_subopList_byOpType, {
          operation_type_id: blockData.operation_type_id,
        });
      }
    } else {
      setFormValues({
        operation_number: processConfig?.previous_operation_id
          ? processConfig?.previous_operation_id + 1
          : (processData?.max_operation || 0) + 1,
        dependencies: '',
        operation_name: '',
        is_duration_required: false,
        duration_deviation_rule: 'none',
        operation_type_id: processConfig?.selected_operation_type
          ? processConfig?.selected_operation_type
          : null,
        department_id: null,
        mass: true,
        min_duration: null,
        max_duration: null,
        is_hold_time: false,
        hold_min_duration: null,
        hold_max_duration: null,
        standard_block: processConfig?.selected_standard_block
          ? processConfig?.selected_standard_block
          : null,
        ingredients: [],
        monitoring: [],
        products: [],
        action_list: [],
        checklist: [],
        instructions: [],
        sampling: [],
        line_clearance: [],
        api_calculation: [],
        machines: [],
        operation_file_config: [],
        sub_op_file_config: {}, // kept for backward compat; per-item file_config is now on each item
      });
      if (processConfig?.selected_standard_block) {
        get_standard_blocks_data(processConfig?.selected_standard_block);
      }
      if (processConfig?.selected_operation_type) {
        get_sub_operation_by_operation_type(success_get_subopList_byOpType, {
          operation_type_id: processConfig?.selected_operation_type,
        });
      }
    }
  }, [processConfig?.operation_id, processConfig?.process_id, processConfig?.previous_operation_id, processConfig?.selected_operation_type, processConfig?.selected_standard_block, processData?.max_operation, from, blockData]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      from !== 'config' ||
      !copyFromOperationId ||
      processConfig?.operation_id ||
      fromView
    ) {
      return;
    }
    get_operation_data_v1((res) => {
      if (res?.data?.status !== 'success' || !res?.data?.data) return;
      const source = res.data.data;
      setFormValues((prev) => ({
        ...prev,
        ...source,
        operation_number: prev?.operation_number,
        dependencies: prev?.dependencies || '',
        department_id: null,
      }));
      if (source?.operation_type_id) {
        get_sub_operation_by_operation_type(success_get_subopList_byOpType, {
          operation_type_id: source.operation_type_id,
        });
      }
    }, { operation_id: copyFromOperationId });
  }, [copyFromOperationId, from, fromView, processConfig?.operation_id]);

  // Library process id (recipe-less). The URL is /process-config/library/:libId;
  // processConfig carries it once a process is selected. Used to load the
  // dependent-operations list in library mode (no recipe to scope by).
  const library_process_id = params?.libId || processConfig?.process_id || null;

  // Feed the dependency-list handler with a single library process by wrapping
  // it in the [process] shape the recipe handler expects.
  const loadLibraryDependencies = () => {
    if (!library_process_id) return;
    get_process_with_operations((res) => {
      if (res?.data?.status === 'success' && res?.data?.data) {
        // get_process_with_operations returns a single process with an
        // `operations` (plural) key; the recipe handler expects a list of
        // processes each with an `operation` (singular) key. Normalise both.
        const p = res.data.data;
        const normalised = { ...p, operation: p.operation || p.operations || [] };
        const wrapped = { ...res, data: { ...res.data, data: [normalised] } };
        successget_process_operation_by_recipe(wrapped);
      }
    }, { process_id: library_process_id });
  };

  useEffect(() => {
    // Recipe mode: scope by recipe_id. Library mode: recipe_id is null, so load
    // the single library process's operations for the dependency dropdown instead.
    if (!recipe_id) {
      loadLibraryDependencies();
      return;
    }
    get_operation_operation_type(success_get_operation_operation_type, {
      recipe_id,
    });
    get_process_operation_by_recipe(successget_process_operation_by_recipe, {
      recipe_id,
    });
  }, [refreshpage, processConfig]);

  const [initialValues, setFormValues] = useState({
    operation_number: (processData?.max_operation || 0) + 1,
    dependencies: '',
    operation_name: '',
    instruction: '',
    is_duration_required: false,
    duration_deviation_rule: 'none',
    operation_type_id: null,
    department_id: null,
    // Operation-level approval toggles (default false), sent on submit.
    is_supervisor_approval_required: false,
    is_qa_approval_required: false,
    is_esign_required: false,
    is_critical: false,
    mass: true,
    min_duration: null,
    max_duration: null,
    is_hold_time: false,
    hold_min_duration: null,
    hold_max_duration: null,
    ingredients: [],
    monitoring: [],
    products: [],
    action_list: [],
    checklist: [],
    instructions: [],
    sampling: [],
    line_clearance: [],
    api_calculation: [],
    machines: [],
    operation_file_config: [],
    sub_op_file_config: {},
  });

  // for button disable
  const isAnyArrayFilled = Object.entries(initialValues).some(
    ([key, value]) => Array.isArray(value) && value.length > 0
  );
  const [activeTab, setActiveTab] = useState('ingredients');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  // File attachment config modal state — target: 'operation'
  const [fileConfigModal, setFileConfigModal] = useState({ open: false, target: null });
  // Per-item file attachment config (for the currently open add/edit sub-op modal)
  const [itemFileConfig, setItemFileConfig] = useState([]);
  const [itemFileConfigModalOpen, setItemFileConfigModalOpen] = useState(false);
  const ActiveIcon = sub_operations.find((i) => i.id === activeTab).icon;
  const ActiveComponent = sub_operations?.find((i) => i.id === activeTab).component;
  const ActiveViewComponent = sub_operations.find((i) => i.id === activeTab).view_component;

  const [standard_block_list, set_standard_block_list] = useState([]);
  const [dependant_operations_list, set_dependant_operations_list] = useState([]);
  const [samplingModal, setSamplingModal] = useState({ open: false, opNumber: null, resolve: null });
  // Machine-group selection popup for dependent ops that have multi-machine-group enabled.
  const [machineGroupModal, setMachineGroupModal] = useState({
    open: false,
    opNumber: null,
    options: [],
    resolve: null,
  });
  const [mgModalValue, setMgModalValue] = useState(null);
  // Dependency type chosen in the machine-group popup:
  //   'machine' -> depends on the upstream op only for the picked machine group(s)
  //   'normal'  -> plain dependency on the whole op (selected_machine_group: [])
  const [mgModalDepType, setMgModalDepType] = useState('machine');
  // Map of { [dependent op number]: machine_group_ids[] } chosen by the user.
  // [] means a normal (non machine-specific) dependency on a multi-machine-group op.
  const [selectedMachineGroups, setSelectedMachineGroups] = useState({});
  const [operation_type_list, set_operation_type_list] = useState([]);
  const [filteredSubopList, setFilteredSubopList] = useState([]);
  // sub_operation label -> stage (PRE / EXECUTION / POST), set from the
  // operation-type's sub_operation config. Drives the per-stage tab grouping.
  const [subopStageMap, setSubopStageMap] = useState({});
  // Active stage in the segmented control (Pre / Execution / Post). Only the
  // chosen stage's sub-op tabs are shown — user fills + 'Saves' one stage at
  // a time. Operation Save stays disabled until every populated stage is saved.
  const [selectedStage, setSelectedStage] = useState('EXECUTION');
  const [opTypeRules, setOpTypeRules] = useState({}); // locked rule items per sub-op from operation type config
  const [inventory_list, set_inventory_list] = useState([]);
  const [parameter_list, set_parameter_list] = useState([]);
  const [sensor_list, set_sensor_list] = useState([]);
  const [role_list, set_role_list] = useState([]);
  const [actions_field_list, set_actions_field_list] = useState([]);
  const [device_list, set_device_list] = useState([]);
  const [testing_type_list, set_testing_type_list] = useState([]);
  const [department_list, set_department_list] = useState([]);
  const [clearance_type_list, set_clearance_type_list] = useState([]);
  const [cleaning_type_list, set_cleaning_type_list] = useState([]);
  const [calculation_field_list, set_calculation_field_list] = useState([]);
  // Active Formula Definitions from the Formula Library (Configuration →
  // Formula) — API Calculation's "Formula" field picks one of these by
  // code/name rather than embedding its own formula text.
  const [formula_definition_list, set_formula_definition_list] = useState([]);
  const [unit_list, set_unit_list] = useState([]);
  const [selectedBlockData, setSelectedBlockData] = useState([]);
  const [visibleSubOps, setVisibleSubOps] = useState([]);
  const [machine_type_list, set_machine_type_list] = useState([]);
  const [machine_list, set_machine_list] = useState([]);
  const [machineTypeWithGroups, setMachineTypeWithGroups] = useState([]);
  const [resistor_list, set_resistor_list] = useState([]);
  const [app_settings, set_app_settings] = useState([]);

  // Callback function to fetch resistors when machine is selected
  const handleMachineSelect = (machine_id) => {
    if (machine_id) {
      get_register_by_machine(
        (res) => {
          if (res.data.status === 'success') {
            set_resistor_list(res.data.data);
          }
        },
        { machine_id }
      );
    } else {
      set_resistor_list([]); // Clear resistors when no machine is selected
    }
  };

  // Original qty of the row open in the modal (0 when adding). Used so the live counters
  // measure the NET change of this row, not its absolute value.
  //   ...Configured...: only when the row is the configured ingredient.
  //   ...Master...: the row's qty regardless of ingredient (master counts everything).
  const editingRowConfiguredOriginalRef = React.useRef(0);
  const editingRowMasterOriginalRef = React.useRef(0);

  // Live per-keystroke update of both counters:
  //   liveConfigured = committedConfigured − (entered − row's original configured qty)
  //   liveMaster     = committedMaster     − (entered − row's original qty)
  // Recomputed from the current entry each time, so Cancel needs no undo (we reset
  // live* back to committed* on cancel).
  const onIngredientQtyChange = (newQty, _prevQty, ingredientIds) => {
    const entered = parseFloat(newQty) || 0;
    // Master counter — every ingredient counts toward the master batch quantity.
    if (committedMaster !== null) {
      const masterOriginal = editingRowMasterOriginalRef.current || 0;
      setLiveMaster(parseFloat((committedMaster - (entered - masterOriginal)).toFixed(4)));
    }
    // Configured counter — only when this row is the configured ingredient.
    if (committedConfigured !== null && configuredIngredientId) {
      const ids = Array.isArray(ingredientIds) ? ingredientIds : [ingredientIds];
      const isConfigured = ids.map(String).includes(String(configuredIngredientId));
      if (isConfigured) {
        const original = editingRowConfiguredOriginalRef.current || 0;
        setLiveConfigured(parseFloat((committedConfigured - (entered - original)).toFixed(4)));
      }
    }
  };

  const modalProps = {
    inventory_list,
    parameter_list,
    sensor_list,
    actions_field_list,
    device_list,
    testing_type_list,
    department_list,
    clearance_type_list,
    cleaning_type_list,
    calculation_field_list,
    formula_definition_list,
    unit_list,
    machine_type_list,
    machine_list,
    resistor_list,
    app_settings,
    role_list,
    roles: role_list,    // alias for ActionList which expects `roles` prop
    handleMachineSelect, // Pass callback function to child components
    // Lets ActionList's inline "Add Action" quick-create refresh the
    // dropdown immediately, without leaving this screen for the Forms
    // library page.
    refreshActionLibrary: () => get_actionlist_data(success_get_actionlist_data),
    has_monitoring_subop: (filteredSubopList || []).some((label) =>
      subOpLabelMatches('Monitoring', label)
    ),
    ...(activeTab === 'ingredients'
      ? {
        onQuantityChange: onIngredientQtyChange,
        remainingQty,
        // Recipe mode (one per recipe). When input-based, only the configured
        // ingredient is limited; otherwise every ingredient uses the master remaining.
        isInputBasedRecipe: !!configuredIngredientId,
        configuredIngredientId,
        configuredIngredientName,
        // Whether this operation's sub-operation list includes "Product".
        // Drives the Discharge → Material Type options (WIPM-only when no Product).
        hasProductSubop: (filteredSubopList || []).some((label) =>
          subOpLabelMatches('Product', label)
        ),

        committedConfigured,
        editingRowConfiguredOriginal: editingRowConfiguredOriginalRef.current,
        committedMaster,
        editingRowMasterOriginal: editingRowMasterOriginalRef.current,
      }
      : {}),
  };

  const success_get_material_inventory = (res) => {
    if (res.data.status === 'success') {
      set_inventory_list(res.data.data);
    }
  };

  const success_get_parameters = (res) => {
    if (res.data.status === 'success') {
      set_parameter_list(res.data.data);
    }
  };

  const success_get_sensor_list = (res) => {
    if (res.data.status === 'success') {
      set_sensor_list(res.data.data);
    }
  };

  const success_get_actionlist_data = (res) => {
    if (res.data.status === 'success') {
      set_actions_field_list(res.data.data);
    }
  };

  // const success_get_device_details = (res) => {
  //   if (res.data.status === 'success') {
  //     set_device_list(res.data.data);
  //   }
  // };

  const success_get_all_testing_type = (res) => {
    if (res.data.status === 'success') {
      set_testing_type_list(res.data.data);
    }
  };

  const success_get_departments = (res) => {
    if (res.status === 200) {
      set_department_list(res.data.departdata);
    }
  };

  const success_get_cleaning_type_for_line = (res) => {
    if (res.data.status === 'success') {
      set_cleaning_type_list(res.data.data);
    }
  };

  const success_get_clearence_type = (res) => {
    if (res.data.status === 'success') {
      set_clearance_type_list(res.data.data);
    }
  };

  const success_get_calculation_field = (res) => {
    if (res.data.status === 'success') {
      set_calculation_field_list(res.data.data);
    }
  };

  const success_get_unit_list = (res) => {
    if (res.data.status === 'success') {
      set_unit_list(res.data.data);
    }
  };

  // get_formula_definitions returns the raw array directly (DRF ViewSet list,
  // no {status,data} envelope) — only ACTIVE formulas are assignable, matching
  // Formula Library's own "Retire → can no longer be assigned to new usages" rule.
  const success_get_formula_definitions = (data) => {
    set_formula_definition_list((Array.isArray(data) ? data : []).filter((f) => f.status === 'active'));
  };

  const success_get_machine_types = (res) => {
    if (res.data.status === 'success') {
      set_machine_type_list(res.data.data);
    }
  };

  useEffect(() => {
    get_machine_category_with_groups((res) => {
      setMachineTypeWithGroups(res?.data?.data || []);
    });
  }, []);

  const success_get_register_machine = (res) => {
    if (res.data.status === 'success') {
      set_machine_list(res.data.data);
    }
  };

  const success_get_standard_blocks = (res) => {
    if (res.data.status === 'success') {
      set_standard_block_list(res.data.data);
    }
  };

  const successget_process_operation_by_recipe = (res) => {
    const treeData = (rawData) => {
      return rawData?.map((process) => ({
        title: process?.process_name,
        value: `process_${process?.process_id || process?.process_name}`,
        key: `process_${process?.process_id || process?.process_name}`,
        selectable: false,
        checkable: false,
        disableCheckbox: true,
        children: process?.operation?.map((operation) => ({
          title: (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              {`Op ${operation.operation_number} - ${operation.operation_type || operation.operation_name || ''
                }`}
              {operation?.is_pass_dependancy && (
                <span
                  style={{
                    fontSize: 10,
                    padding: '0 4px',
                    borderRadius: 3,
                    background: '#dcfce7',
                    color: '#16a34a',
                    fontWeight: 600,
                  }}
                >
                  Sampling Pass
                </span>
              )}
              {operation?.is_fail_dependancy && (
                <span
                  style={{
                    fontSize: 10,
                    padding: '0 4px',
                    borderRadius: 3,
                    background: '#fee2e2',
                    color: '#dc2626',
                    fontWeight: 600,
                  }}
                >
                  Sampling Fail
                </span>
              )}
            </span>
          ),
          value: `${operation?.operation_number.toString()}`,
          key: operation?.operation_number?.toString(),
          selectable: true,
          is_sampling: !!operation?.is_sampling,
          is_pass_dependancy: !!operation?.is_pass_dependancy,
          is_fail_dependancy: !!operation?.is_fail_dependancy,
          is_multi_machine_group: !!operation?.is_multi_machine_group,
          // Backend returns machine_groups_list (combined); keep machine_groups as fallback.
          machine_groups: operation?.machine_groups_list || operation?.machine_groups || [],
          opType: operation.operation_type || operation.operation_name || '',
        })),
      }));
    };
    if (res?.data?.status === 'success') {
      if (res?.data?.data && res?.data?.data?.length > 0) {
        // for operation edit and add operation between based on that dependacy
        //  list should filter
        let processMaxop = null;
        let procData = null;
        if (
          processConfig?.process_id ||
          processConfig?.operation_id ||
          processConfig?.previous_operation_id
        ) {
          if (processConfig?.process_id) {
            procData = res?.data?.data?.find(
              (data) => data?.process_id === processConfig?.process_id
            ); // get process oprlist
            if (procData && procData?.operation && procData?.operation?.length > 0) {
              processMaxop = procData?.operation?.find(
                (data) => data?.operation_id === processConfig?.operation_id
              )?.operation_number; // find selected operation number
            }
          }

          const process_max_operation = procData?.max_operation;
          const trimmedProcesses = trimProcessesByOperationNumber(
            res?.data?.data,
            processConfig?.previous_operation_id
              ? processConfig?.previous_operation_id
              : processConfig?.operation_id
                ? processMaxop - 1
                : process_max_operation
          ); // for trim for less less than operationss
          set_dependant_operations_list(treeData(trimmedProcesses));
        } else {
          set_dependant_operations_list(treeData(res?.data?.data));
        }
      }
    }
  };

  const success_get_operation_type = (res) => {
    if (res.data.status === 'success') {
      set_operation_type_list(res.data.data);
    }
  };

  const success_get_subopList_byOpType = (res) => {
    if (res?.data?.status === 'success') {
      const filteredData = res?.data?.data
        ? res?.data?.data?.map((item) => item?.sub_operation)
        : [];
      setFilteredSubopList(filteredData || []);
      // Capture ALL stages per sub-op label.
      // A sub-op can belong to multiple stages (e.g. "Action List" in EXECUTION and POST).
      // stageMap shape: { normalizedLabel: Set<'PRE'|'EXECUTION'|'POST'> }
      // Key by NORMALIZED label so lookups survive capitalisation/plural differences.
      const stageMap = {};
      (res?.data?.data || []).forEach((item) => {
        if (item?.sub_operation) {
          const key = normalizeSubOpLabel(item.sub_operation);
          const stage = (item.stage || 'EXECUTION').toUpperCase();
          if (!stageMap[key]) stageMap[key] = new Set();
          stageMap[key].add(stage);
        }
      });
      setSubopStageMap(stageMap);
      // Default the segmented control to the first populated stage
      // (PRE > EXECUTION > POST priority).
      const present = new Set();
      Object.values(stageMap).forEach((set) => set.forEach((s) => present.add(s)));
      const firstPopulated = ['PRE', 'EXECUTION', 'POST'].find((s) => present.has(s)) || 'EXECUTION';
      setSelectedStage(firstPopulated);

      // Auto-apply operation-type level flags (top-level in response)
      // These default the toggles whenever an operation type is selected or loaded in edit mode.
      const isCritical = !!res?.data?.is_critical;
      setFormValues((prev) => ({
        ...prev,
        is_critical: isCritical,
        is_esign_required: processConfig?.operation_id
          ? !!prev?.is_esign_required
          : !!res?.data?.is_esign_required,
        is_supervisor_approval_required: processConfig?.operation_id
          ? isCritical || !!prev?.is_supervisor_approval_required
          : isCritical || !!res?.data?.is_supervisor_approval_required,
        is_qa_approval_required: processConfig?.operation_id
          ? !!prev?.is_qa_approval_required
          : !!res?.data?.is_qa_approval_required,
      }));

      // Extract sub_operation_rules from the top-level response key and seed locked items into form
      const rules = res?.data?.sub_operation_rules || {};
      if (Object.keys(rules).length > 0) {
        // Tag each rule item with _from_rule so we can lock it in the UI
        // Normalize keys to match sub_operations[].id (lowercase snake_case)
        const normKey = (k) => {
          const lower = k.toLowerCase().replace(/[\s-]+/g, '_');
          const match = sub_operations.find(
            (s) => s.id === lower || s.label.toLowerCase() === k.toLowerCase()
          );
          return match?.id || lower;
        };
        const taggedRules = {};
        Object.entries(rules).forEach(([key, items]) => {
          if (Array.isArray(items) && items.length > 0) {
            taggedRules[normKey(key)] = items.map((item) => ({ ...item, _from_rule: true }));
          }
        });
        setOpTypeRules(taggedRules);
      } else {
        setOpTypeRules({});
      }
    } else {
      setFilteredSubopList([]);
      setSubopStageMap({});
      setOpTypeRules({});
    }
  };

  const get_process_data_for_process_byId = async (processId) => {
    await get_process_data_for_process_id(
      (res) => {
        if (res?.data?.status === 'success' && res?.data?.data && res?.data?.data?.length > 0) {
          setProcessData(res?.data?.data[0]);
        } else {
          setProcessData({});
        }
      },
      {
        process_id: processId,
      }
    );
  };

  const get_standard_blocks_data = async (value) => {
    // Single-select picker (not a paginated table) — ask for the full
    // unpaginated list instead of the backend's default 15-per-page.
    let param = value
      ? { operation_type_id: value, module_type: selectedModule, all: true }
      : { module_type: selectedModule, all: true };
    try {
      await get_standard_blocks((res) => {
        if (res?.data?.status && res.data.status !== 'failed') {
          const blockData = Array.isArray(res?.data?.data) ? res.data.data : [];
          setSelectedBlockData(blockData);

          // extract first nested data safely
          const apiValues =
            blockData.length > 0 &&
              Array.isArray(blockData[0]?.data) &&
              blockData[0].data.length > 0
              ? blockData[0].data[0]
              : {};

          setFormValues((prev) => ({
            ...prev,
            ...apiValues, // spread API values
            operation_number: processConfig?.previous_operation_id
              ? processConfig?.previous_operation_id + 1
              : (processData?.max_operation || 0) + 1,
            standard_block: processConfig?.selected_standard_block, // add overwrite field
          }));

          // Note: remaining is now derived from the stable backend baseline minus the
          // operation's UI ingredients (see recomputeFromIngredients / the form preview),
          // so loading a standard block's ingredients needs no manual decrement here.

          if (apiValues?.operation_type_id) {
            get_sub_operation_by_operation_type(success_get_subopList_byOpType, {
              operation_type_id: apiValues.operation_type_id,
            });
          }
        } else {
          setSelectedBlockData([]);
        }
      }, param);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  useEffect(() => {
    get_material_inventory(success_get_material_inventory, {
      module_type: 'prod',
    });
    get_parameters(success_get_parameters);
    get_sensor_list(success_get_sensor_list);
    get_actionlist_data(success_get_actionlist_data);
    // get_device_details(success_get_device_details);
    get_all_testing_type(success_get_all_testing_type);
    get_departments(success_get_departments);
    get_cleaning_type_for_line(success_get_cleaning_type_for_line);
    get_clearence_type(success_get_clearence_type);
    get_calculation_field(success_get_calculation_field);
    get_formula_definitions(success_get_formula_definitions);
    get_units(success_get_unit_list);
    get_machine_types(success_get_machine_types);
    get_register_machine(success_get_register_machine);
    get_list_of_setting((res) => {
      if (res?.data?.data) set_app_settings(res.data.data);
    });
    get_standard_blocks(success_get_standard_blocks, { all: true });
    // Load the dependent-operations list: by recipe in recipe mode, or by the
    // single library process in library mode (recipe_id is null).
    if (recipe_id) {
      // Recipe mode: run the shared dependency handler AND fetch op-types once
      // here with the recipe's fresh is_packaging flag (from the same response),
      // avoiding both a stale default and a duplicate call from other effects.
      get_process_operation_by_recipe((res) => {
        successget_process_operation_by_recipe(res);
        const pkg = res?.data?.is_packaging !== undefined
          ? !!res.data.is_packaging
          : selectedModule === 'pack';
        get_operation_type(success_get_operation_type, { is_packaging: pkg });
      }, { recipe_id: recipe_id });
    } else {
      loadLibraryDependencies();
      // Library mode (recipe-less): no recipe flag from the API, use the module.
      get_operation_type(success_get_operation_type, { is_packaging: selectedModule === 'pack' });
    }
    get_roles((res) => { if (res?.data?.data) set_role_list(res.data.data); });
  }, []);

  useEffect(() => {
    if (processConfig?.process_id) {
      get_process_data_for_process_byId(processConfig?.process_id);
    }
  }, [processConfig?.process_id]); // eslint-disable-line react-hooks/exhaustive-deps

  //For showing suboperations based on operation type
  useEffect(() => {
    if (sub_operations?.length) {
      const filtered = sub_operations?.filter((op) =>
        (filteredSubopList || []).some((label) => subOpLabelMatches(op?.label, label))
      ) ?? [];

      if (filtered?.length > 0) {
        setVisibleSubOps(filtered);
        setActiveTab(filtered[0]?.id);
      } else {
        setVisibleSubOps(sub_operations?.slice(0, 8));
        setActiveTab(sub_operations?.[0]?.id);
      }
    }
  }, [sub_operations, filteredSubopList]);
  return (
    <div className="flex flex-col h-full">
      {from === 'config' && (
        <div
          className="rounded-lg shadow p-4 mb-2 min-w-full flex-shrink-0"
          style={{ backgroundColor: cardBgColor }}
        >
          <div className="flex flex-wrap items-center justify-between gap-3 ml-2">
            <div className="flex flex-wrap items-center gap-4">
              {Object.entries(processData)
                .filter(([key]) => ['areas', 'roles', 'approvals'].includes(key))
                .map(([key, value]) => {
                  const formattedKey = key
                    .split('_')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');

                  return (
                    <div key={key} className="flex items-center gap-1 mr-2">
                      <span className="font-small text-[16px]" style={{ color: textColor }}>
                        {`Process ${formattedKey}`}:
                      </span>

                      {Array.isArray(value) ? (
                        value.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {value.map((item, i) => (
                              <span
                                key={i}
                                className="inline-flex text-[13.5px] items-center px-2 py-0.5 rounded-md text-xs font-small"
                                style={{
                                  backgroundColor: isDark ? '#1e3a5f' : '#eff6ff',
                                  color: isDark ? '#93c5fd' : '#1d4ed8',
                                }}
                              >
                                {item}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span
                            className="text-sm text-[13px] italic"
                            style={{ color: secondaryTextColor }}
                          >
                            Not Provided
                          </span>
                        )
                      ) : value ? (
                        <span className="text-sm text-[13px]" style={{ color: textColor }}>
                          {value}
                        </span>
                      ) : (
                        <span
                          className="text-sm text-[13px] italic"
                          style={{ color: secondaryTextColor }}
                        >
                          Not Provided
                        </span>
                      )}
                    </div>
                  );
                })}
            </div>

            <div
              title={isShowDag ? 'Close Workflow' : 'View Workflow'}
              className="flex gap-2 items-center flex-shrink-0"
            >
              {configuredIngredientName && (liveConfigured ?? configuredIngredientRemaining) !== null ? (
                <div
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium shadow-sm"
                  style={{
                    backgroundColor: cardBgColor,
                    border: `1px solid ${(liveConfigured ?? configuredIngredientRemaining) < 0 ? '#fca5a5' : borderColor}`,
                    color: (liveConfigured ?? configuredIngredientRemaining) < 0 ? '#dc2626' : textColor,
                  }}
                >
                  <span style={{ color: secondaryTextColor, fontWeight: 500 }}>{configuredIngredientName}:</span>
                  <span className="font-semibold">{parseFloat(liveConfigured ?? configuredIngredientRemaining).toFixed(2)}</span>
                  <span style={{ color: secondaryTextColor }}>{remainingUnit}</span>
                  <span style={{ color: secondaryTextColor, fontSize: 11 }}>remaining</span>
                </div>
              ) : (
                (liveMaster ?? remainingQty) !== null &&
                (liveMaster ?? remainingQty) != null && (
                  <div
                    className="flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium shadow-sm"
                    style={{
                      backgroundColor: cardBgColor,
                      border: `1px solid ${(liveMaster ?? remainingQty) < 0 ? '#fca5a5' : borderColor}`,
                      color: (liveMaster ?? remainingQty) < 0 ? '#dc2626' : textColor,
                    }}
                  >
                    <span style={{ color: secondaryTextColor }}>Remaining:</span>
                    <span className="font-semibold">{parseFloat(liveMaster ?? remainingQty).toFixed(2)}</span>
                    {remainingUnit && <span style={{ color: secondaryTextColor }}>{remainingUnit}</span>}
                  </div>
                )
              )}
              {MbomButton()}
              {WorkFlowButton()}
              {/* RDY-FE: readiness submit control — "Submit — N blockers" while anything
                  blocks, "Submit for approval" when the recipe is clean.
                  RDY-LIFE: it now reads the record's approval state too, and renders a status
                  pill instead of a button where the state forbids submission. The return
                  action sits beside it and shows itself only to QA on a submitted record. */}
              <ReturnForCorrectionButton recipeId={recipe_id} />
              <ReadinessSubmitButton recipeId={recipe_id} />
            </div>
          </div>
        </div>
      )}
      {isAddOperation || isBlockLikeForm || isLocked ? (
        processConfig?.operation_id || isAddOperation || isBlockLikeForm ? (
          // On the Standard Block page the page card is the one scroller (thin scrollbar); a second scroll area here made two nested scrollbars. Everywhere else this keeps its own scroll.
          <div className={from === 'standard_block' ? 'relative flex-1' : `relative flex-1 overflow-y-auto${isBlockLikeForm ? ' page-card-scroll' : ''}`}>
            {/* Basic Information */}
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={(values, { resetForm }) => {
                // ── Clear, field-specific validation on submit ──
                // (The button is always clickable; we surface exactly what's
                //  missing here instead of silently disabling it.)
                if (!values.operation_type_id) {
                  toast.warning('Please select an Operation Type.');
                  return;
                }
                if (!values.operation_name || !String(values.operation_name).trim()) {
                  toast.warning('Please enter an Operation Name.');
                  return;
                }

                // Which stages this operation type requires (from its sub-op config).
                const requiredStages = new Set();
                Object.values(subopStageMap || {}).forEach((set) => {
                  if (set instanceof Set) set.forEach((s) => requiredStages.add(s));
                  else if (typeof set === 'string') requiredStages.add(set.toUpperCase());
                });
                const stageName = { PRE: 'Pre', EXECUTION: 'Execution', POST: 'Post' };
                const SUBOP_IDS = (sub_operations || []).map((t) => t.id);
                const stageHasItem = (stageKey) =>
                  SUBOP_IDS.some((field) =>
                    Array.isArray(values?.[field]) &&
                    values[field].some(
                      (item) => (item?.stage || 'EXECUTION').toUpperCase() === stageKey
                    )
                  );

                if (requiredStages.size > 0) {
                  // Op type has configured sub-ops: every required stage needs ≥1 item.
                  const missing = [...requiredStages].filter((s) => !stageHasItem(s));
                  if (missing.length > 0) {
                    toast.warning(
                      `Please add at least one sub-operation to the ${missing
                        .map((s) => stageName[s] || s)
                        .join(', ')} stage${missing.length > 1 ? 's' : ''}.`
                    );
                    return;
                  }
                } else if (!validateArrayFields(values)) {
                  // No stage config resolved yet — fall back to the generic check.
                  toast.warning('Please fill in the sub-operation details before saving.');
                  return;
                }

                // Compute operation_code in the UI: `<process_code>_<operation_type_code>`
                const processCode = processData?.process_code || '';
                const opTypeCode =
                  (operation_type_list || []).find(
                    (t) => String(t.operation_type_id) === String(values.operation_type_id)
                  )?.code || '';
                const operationCode =
                  processCode && opTypeCode ? `${processCode}_${opTypeCode}` : null;
                // Operation-level flag: true when the Machine Setup rule for this
                // operation type has "Multi Select Machine Per Operation" enabled.
                const isMultiMachineGroup = (opTypeRules?.machines || []).some(
                  (r) => r?.multi_select_machine_per_operation === true
                );
                // Machine groups the user picked for multi-machine-group dependent
                // operations. Only include deps that are still selected.
                const depBaseNums = (values.dependencies || '')
                  .split(',')
                  .filter(Boolean)
                  .map((d) => d.replace(/_pass$|_fail$/, ''));
                const selectedMachineGroupPayload = Object.entries(selectedMachineGroups)
                  .filter(([opNum]) => depBaseNums.includes(String(opNum)))
                  .map(([opNum, ids]) => ({
                    operation_number: Number(opNum),
                    selected_machine_group: ids,
                  }));
                // Tag every item with its stage. Prefer the item's own `stage`
                // (set when the row was added under a particular stage). Fall
                // back to the operation-type's subopStageMap, then 'EXECUTION'.
                // This is how the same sub-op (e.g. Ingredients) can have rows
                // distributed across PRE / EXECUTION / POST without losing
                // per-row stage info on submit. Backend can ignore the field
                // today; persists when ready. Frontend-only change.
                const stageForTabId = (tabId) => {
                  const tab = (sub_operations || []).find((t) => t.id === tabId);
                  const label = tab?.label;
                  return (subopStageMap?.[label] || 'EXECUTION').toUpperCase();
                };
                const SUBOP_ARRAY_FIELDS = (sub_operations || []).map((t) => t.id);
                const stagedValues = { ...values };
                SUBOP_ARRAY_FIELDS.forEach((field) => {
                  if (Array.isArray(stagedValues[field])) {
                    const fallbackStage = stageForTabId(field);
                    stagedValues[field] = stagedValues[field].map((item) => {
                      if (!item || typeof item !== 'object') return item;
                      const itemStage = (item.stage || '').toString().toUpperCase();
                      return {
                        ...item,
                        stage: itemStage || fallbackStage,
                      };
                    });
                  }
                });

                const valuesWithCode = {
                  ...stagedValues,
                  operation_code: values.operation_code || operationCode,
                  // Operation-level instruction / description text
                  instruction: values.instruction || '',
                  min_duration: values.is_duration_required ? values.min_duration : null,
                  max_duration: values.is_duration_required ? values.max_duration : null,
                  // Operation-level hold time (independent of duration-required).
                  // Sent as HH:MM:SS; default "00:00:00" when not set. The
                  // TimePicker holds HH:MM, so append ":00" for the seconds.
                  hold_min_time: values.hold_min_duration
                    ? `${values.hold_min_duration}:00`
                    : '00:00:00',
                  hold_max_time: values.hold_max_duration
                    ? `${values.hold_max_duration}:00`
                    : '00:00:00',
                  // Duration deviation rule applies only when duration is required.
                  duration_deviation_rule: values.is_duration_required
                    ? (values.duration_deviation_rule || 'none')
                    : 'none',
                  is_multi_machine_group: isMultiMachineGroup,
                  dependent_machine_groups: selectedMachineGroupPayload,
                  // Operation-level approval toggles (default false).
                  is_supervisor_approval_required: !!values.is_supervisor_approval_required,
                  is_qa_approval_required: !!values.is_qa_approval_required,
                  is_esign_required: !!values.is_esign_required,
                  // File attachment requirements configured for this operation and its sub-ops
                  operation_file_config: values.operation_file_config || [],
                  sub_op_file_config: values.sub_op_file_config || {},
                  // Apply QC/inline mutual exclusion inside each sampling item
                  sampling: (stagedValues.sampling || []).map((item) => ({
                    ...item,
                    is_qc_sampling: !!item.is_qc_sampling,
                    is_inline_sampling: item.is_qc_sampling ? false : true,
                  })),
                  api_calculation: (stagedValues.api_calculation || []).map((item) => ({
                    ...item,
                    process_api_calculation_id: item.process_api_calculation_id ?? null,
                    formula_definition_id: item.formula_definition_id ?? null,
                    unit_id: item.unit_id ?? null,
                    min_value: item.min_value != null ? Number(item.min_value) : null,
                    max_value: item.max_value != null ? Number(item.max_value) : null,
                  })),
                };

                const success_add_operation_data_v1 = (res) => {
                  if (res?.data?.status === 'success') {
                    toast?.success(res?.data?.message);
                    dispatch(toggleRefresh());
                    // Re-pull the authoritative remaining (master − all ingredients across
                    // all operations) now that this operation is persisted.
                    fetchRemainingQty();
                    // RDY-FE: refresh readiness for THIS operation only (contract's
                    // ?operation_id= form) and splice it into the tree — never the whole recipe.
                    const savedId = processConfig?.operation_id || res?.data?.data?.[0]?.operation_id;
                    if (savedId) readiness?.refreshOperation?.(savedId);
                  } else {
                    toast?.warning(res?.data?.message);
                  }
                };
                if (from === 'config') {
                  if (processConfig?.operation_id) {
                    edit_operation_data_v1(success_add_operation_data_v1, {
                      operation_id: processConfig?.operation_id,
                      operation_code: valuesWithCode.operation_code || null,
                      operations: [valuesWithCode],
                    });
                  } else {
                    add_operation_data_v1(success_add_operation_data_v1, {
                      process_id: processConfig.process_id,
                      operation_code: valuesWithCode.operation_code || null,
                      ...(processConfig.previous_operation_id || processData?.max_operation
                        ? {
                          previous_operation: processConfig.previous_operation_id
                            ? processConfig.previous_operation_id
                            : processData?.max_operation,
                        }
                        : {}),
                      operations: [valuesWithCode],
                    });
                  }
                } else {
                  handleBlockSubmit(valuesWithCode);
                }
                // resetForm();
              }}
              enableReinitialize
            >
              {({ values, errors, touched, setFieldValue, handleSubmit }) => {
                // Rule fields (execution_mode, mode, instruction) from operation type config,
                // with the Process Type's rules (if the process has one assigned) layered on
                // top — an additional, stricter locking layer set once per process type rather
                // than repeated on every Operation Type. Process Type wins on any field both
                // layers touch; in practice they're not expected to overlap (see
                // ProcessTypeConfig.js — same rule shape, different admin screen).
                // Passed as lockedRuleFields to the form so those specific fields are pre-filled and disabled
                const ruleDefaults = (() => {
                  const opTypeItem = opTypeRules[activeTab]?.[0];
                  const processTypeItem = processData?.process_type_rules?.[activeTab]?.[0];
                  if (!opTypeItem && !processTypeItem) return undefined;
                  const { _from_rule: _f1, ...opTypeDefaults } = opTypeItem || {};
                  const { _from_rule: _f2, ...processTypeDefaults } = processTypeItem || {};
                  return { ...opTypeDefaults, ...processTypeDefaults };
                })();
                const modal_values = editIndex !== null ? values[activeTab][editIndex] : undefined;
                const linkedMachineSetupEnabled = (filteredSubopList || []).some(
                  (label) => subOpLabelMatches('Machine Setup', label)
                );
                const requiresMachineSetupBeforeAdd =
                  linkedMachineSetupEnabled &&
                  // Monitoring can target a Location instead of a Machine (its own
                  // Target Type selector handles that), so it doesn't need Machine
                  // Setup configured first the way Ingredients does.
                  ['ingredients'].includes(activeTab) &&
                  (values.machines || []).length === 0;
                const modal_submit = (updatedValues) => {
                  // Machine Setup multi-select returns { machines: [ {…}, {…} ] }.
                  // Store it as ONE entry (single card) holding its machines array,
                  // stripping the duplicate flat machine_type_id/group fields.
                  const normalize = (val) => {
                    if (activeTab === 'machines') {
                      // Preserve top-level machine_type_id, machine_group_id, machine_ids, machine_ids_csv
                      return {
                        machine_type_id: val?.machine_type_id ?? null,
                        machine_group_id: val?.machine_group_id ?? null,
                        machine_type: val?.machine_type ?? null,
                        machine_id: val?.machine_id ?? null,
                        machine_ids: val?.machine_ids ?? [],
                        machine_ids_csv: val?.machine_ids_csv ?? '',
                        execution_rules: val?.execution_rules ?? null,
                        ...(Array.isArray(val?.machines) && val.machines.length > 0 ? {
                          machines: val.machines.map((m) => ({
                            machine_type_id: m.machine_type_id ?? null,
                            machine_group_id: m.machine_group_id ?? null,
                            machine_type: m.machine_type ?? null,
                          })),
                        } : {}),
                      };
                    }
                    return val;
                  };

                  // Stamp each item with the stage it was added under so the same
                  // sub-op type (e.g. Ingredients) can be distributed across
                  // multiple stages without losing per-row stage info on submit.
                  const stagedEntry = (() => {
                    const base = normalize(updatedValues);
                    const stampStage = (item) => {
                      if (!item || typeof item !== 'object') return item;
                      const existingStage = item.stage;
                      return {
                        ...item,
                        stage: existingStage || (selectedStage || 'EXECUTION'),
                        file_config: itemFileConfig,
                      };
                    };
                    if (Array.isArray(base)) return base.map(stampStage);
                    if (!base || typeof base !== 'object') return base;
                    const existingStage = base.stage;
                    return {
                      ...base,
                      stage: existingStage || (selectedStage || 'EXECUTION'),
                      file_config: itemFileConfig,
                    };
                  })();
                  const entry = stagedEntry;
                  if (editIndex !== null) {
                    const updated = [...values[activeTab]];
                    updated[editIndex] = entry;
                    setFieldValue(activeTab, updated);
                  } else {
                    setFieldValue(activeTab, [
                      ...values[activeTab],
                      ...(Array.isArray(entry) ? entry : [entry]),
                    ]);
                  }
                  // Commit both counters: the live value (already net of this row's entry)
                  // becomes the new committed baseline for the next row. The header is also
                  // reconciled with the backend after the operation saves.
                  if (activeTab === 'ingredients') {
                    if (liveConfigured !== null) setCommittedConfigured(liveConfigured);
                    if (liveMaster !== null) setCommittedMaster(liveMaster);
                  }
                  setIsModalOpen(false);
                  setEditIndex(null);
                };

                // Original qty of the configured ingredient for a given row (0 if not it).
                const configuredQtyOfRow = (row) => {
                  if (!row || !configuredIngredientId) return 0;
                  const ids = Array.isArray(row.ingredient_id) ? row.ingredient_id : [row.ingredient_id];
                  return ids.map(String).includes(String(configuredIngredientId))
                    ? (parseFloat(row.quantity) || 0)
                    : 0;
                };
                // Row's qty for the master counter (any ingredient counts).
                const masterQtyOfRow = (row) => (row ? (parseFloat(row.quantity) || 0) : 0);

                const openModal = () => {
                  if (requiresMachineSetupBeforeAdd) {
                    toast.warning('Please setup machines first in the Machine Setup tab.');
                    return;
                  }
                  // Adding a new row → no original qty to free.
                  editingRowConfiguredOriginalRef.current = 0;
                  editingRowMasterOriginalRef.current = 0;
                  setItemFileConfig([]);
                  setIsModalOpen(true);
                };

                const editFunction = (index) => {
                  if (isLocked) return;
                  if (values[activeTab]?.[index]?._from_rule) return; // locked
                  // Editing → remember this row's qty so the live counters measure only the NET change.
                  editingRowConfiguredOriginalRef.current =
                    activeTab === 'ingredients' ? configuredQtyOfRow(values[activeTab]?.[index]) : 0;
                  editingRowMasterOriginalRef.current =
                    activeTab === 'ingredients' ? masterQtyOfRow(values[activeTab]?.[index]) : 0;
                  setItemFileConfig(values[activeTab]?.[index]?.file_config || []);
                  setIsModalOpen(true);
                  setEditIndex(index);
                };

                const deleteFunction = (index) => {
                  if (isLocked) return;
                  if (values[activeTab]?.[index]?._from_rule) return; // locked
                  Modal.confirm({
                    title: 'Are you sure you want to delete this item?',
                    okText: 'Yes, Delete',
                    okType: 'danger',
                    cancelText: 'Cancel',
                    onOk() {
                      try {
                        const removed = values[activeTab]?.[index];
                        const updated = [...values[activeTab]];
                        updated.splice(index, 1);
                        setFieldValue(activeTab, updated);
                        // Deleting a configured-ingredient row frees its quantity back to the
                        // counter (both committed and live).
                        if (activeTab === 'ingredients') {
                          const freed = configuredQtyOfRow(removed);
                          if (freed > 0) {
                            setCommittedConfigured((v) =>
                              v === null ? v : parseFloat((v + freed).toFixed(4))
                            );
                            setLiveConfigured((v) =>
                              v === null ? v : parseFloat((v + freed).toFixed(4))
                            );
                          }
                          // Master counter: free the deleted row's full quantity.
                          const freedMaster = masterQtyOfRow(removed);
                          if (freedMaster > 0) {
                            setCommittedMaster((v) =>
                              v === null ? v : parseFloat((v + freedMaster).toFixed(4))
                            );
                            setLiveMaster((v) =>
                              v === null ? v : parseFloat((v + freedMaster).toFixed(4))
                            );
                          }
                        }
                      } catch (error) {
                        Modal.error({
                          title: 'Delete Failed',
                          content: 'Something went wrong while deleting the item.',
                        });
                      }
                    },
                    onCancel() {
                      console.log('Deletion cancelled');
                    },
                  });
                };

                // Convert operation duration (HH:mm format) to seconds.
                // Use max duration for monitoring because min can validly be 00:00.
                const convertDurationToSeconds = (duration) => {
                  if (!duration) return 0;
                  const [hours, minutes] = duration.split(':').map(Number);
                  return hours * 3600 + minutes * 60;
                };

                const operation_duration_in_seconds = values.is_duration_required
                  ? (
                    convertDurationToSeconds(values.max_duration) ||
                    convertDurationToSeconds(values.min_duration)
                  )
                  : 0;

                // A locked continuous-monitoring rule forces operation duration to 00:00.
                // Editable continuous monitoring is only a per-item default.
                const isContinuousMonitoring = opTypeRules?.monitoring?.some(
                  (r) => r.is_continuous === true && r.is_continuous_editable !== true
                );

                // Auto-reset duration to 00:00 when continuous monitoring is active
                if (
                  isContinuousMonitoring &&
                  (values.max_duration !== '00:00' || values.min_duration !== '00:00')
                ) {
                  setFieldValue('max_duration', '00:00');
                  setFieldValue('min_duration', '00:00');
                }

                // Duration Required, Review & Approve and E-Sign Required — moved
                // into the header row (top-right, before Required File Uploads) as
                // plain inline toggle + label pairs, no background/border box.
                const durationOnHeader = !!values.is_duration_required;
                const durationRequiredNode = (
                  <div className="flex items-center gap-1.5">
                    <span style={{ fontSize: 12.5, color: textColor }}>Duration Required</span>
                    <Switch
                      size="small"
                      checked={durationOnHeader}
                      disabled={isLocked}
                      checkedChildren="Yes"
                      unCheckedChildren="No"
                      onChange={(checked) => {
                        setFieldValue('is_duration_required', checked);
                        if (!checked) {
                          setFieldValue('min_duration', null);
                          setFieldValue('max_duration', null);
                        } else if (!values.min_duration || !values.max_duration) {
                          setFieldValue('min_duration', '00:00');
                          setFieldValue('max_duration', '00:00');
                        }
                      }}
                    />
                  </div>
                );

                const reviewApproveNode = (
                  <div className="flex items-center gap-1.5">
                    <span style={{ fontSize: 12.5, color: textColor }}>Review &amp; Approve</span>
                    <Switch
                      size="small"
                      checked={!!values.is_supervisor_approval_required}
                      disabled={isLocked || !!values.is_critical}
                      checkedChildren="Yes"
                      unCheckedChildren="No"
                      onChange={(checked) =>
                        setFieldValue('is_supervisor_approval_required', checked)
                      }
                    />
                    {values.is_critical && (
                      <span style={{ fontSize: 10, color: '#d46b08', lineHeight: 1.3 }}>
                        Auto
                      </span>
                    )}
                  </div>
                );

                const esignRequiredNode = (
                  <div className="flex items-center gap-1.5">
                    <span style={{ fontSize: 12.5, color: textColor }}>E-Sign Required</span>
                    <Switch
                      size="small"
                      checked={!!values.is_esign_required}
                      disabled={isLocked}
                      checkedChildren="Yes"
                      unCheckedChildren="No"
                      onChange={(checked) =>
                        setFieldValue('is_esign_required', checked)
                      }
                    />
                  </div>
                );

                // Required File Uploads — moved into the header row (top-right,
                // next to the title) per request; the button/count/tooltip
                // behavior is unchanged, only its position moved.
                const requiredFileUploadsNode = (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <FileText size={13} style={{ color: '#6366f1', flexShrink: 0 }} />
                    <span style={{ fontSize: 12.5, color: textColor }}>
                      Required File Uploads
                    </span>
                    {(values.operation_file_config || []).length > 0 && (
                      <Tag style={{ fontSize: 11, margin: 0, background: '#eff0fe', borderColor: '#a5b4fc', color: '#4f46e5' }}>
                        {(values.operation_file_config || []).length} requirement{(values.operation_file_config || []).length !== 1 ? 's' : ''}
                      </Tag>
                    )}
                    <Tooltip
                      title={
                        isLocked
                          ? 'View Files'
                          : (values.operation_file_config || []).length > 0
                            ? 'Edit Requirements'
                            : 'Add Required Files'
                      }
                    >
                      <button
                        type="button"
                        aria-label="Required file uploads"
                        onClick={() => setFileConfigModal({ open: true, target: 'operation' })}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#6366f1',
                          background: isDark ? 'rgba(99,102,241,0.15)' : '#eff0fe',
                          border: '1px solid #a5b4fc',
                          borderRadius: '50%',
                          width: 26,
                          height: 26,
                          cursor: 'pointer',
                        }}
                      >
                        <Paperclip size={13} />
                      </button>
                    </Tooltip>
                  </div>
                );

                // Stage segmented control (Pre / Execution / Post) + Save/Update button.
                // Split into two named nodes so the Save button can render in the
                // header (top-right) while the stage pills stay in the bottom bar.
                const stageControls = (() => {
                  // subopStageMap values are Sets of stages — flatten them all.
                  const presentStages = new Set();
                  Object.values(subopStageMap || {}).forEach((stageSet) => {
                    if (stageSet instanceof Set) stageSet.forEach((s) => presentStages.add(s));
                    else if (typeof stageSet === 'string') presentStages.add(stageSet); // legacy fallback
                  });
                  // Fall back to scanning saved items' stage fields ONLY when the
                  // operation-type map is genuinely unavailable (size 0 — e.g. view
                  // mode before the op-type API resolved). When the op type HAS been
                  // fetched, subopStageMap is authoritative: an execution-only op type
                  // reports exactly {EXECUTION}, and we must NOT re-add PRE/POST from
                  // stale saved rows left over from a previous op-type config — that
                  // is what made the Save/Update button demand Pre & Post stages that
                  // the current op type never configures.
                  if (presentStages.size === 0) {
                    const SUBOP_IDS = (sub_operations || []).map((t) => t.id);
                    SUBOP_IDS.forEach((field) => {
                      if (Array.isArray(values?.[field])) {
                        values[field].forEach((item) => {
                          const s = (item?.stage || '').toString().toUpperCase();
                          if (['PRE', 'EXECUTION', 'POST'].includes(s)) presentStages.add(s);
                        });
                      }
                    });
                  }
                  const stagesToFill = ['PRE', 'EXECUTION', 'POST'].filter((s) => presentStages.has(s));
                  const hasStageInfo = stagesToFill.length > 0;
                  const stageLabel = (k) => ({ PRE: 'Pre', EXECUTION: 'Execution', POST: 'Post' }[k] || k);
                  // Returns the Set of stages a tab belongs to (from subopStageMap).
                  // Falls back to saved items' stage field (edit/view mode).
                  const getTabStages = (tab) => {
                    const fromMap = subopStageMap?.[normalizeSubOpLabel(tab.label)];
                    if (fromMap instanceof Set && fromMap.size > 0) return fromMap;
                    if (typeof fromMap === 'string') return new Set([fromMap.toUpperCase()]);
                    // Fallback: scan saved items for their stage
                    const stages = new Set();
                    const savedItems = values?.[tab.id];
                    if (Array.isArray(savedItems)) {
                      savedItems.forEach((item) => {
                        const s = (item?.stage || '').toString().toUpperCase();
                        if (['PRE', 'EXECUTION', 'POST'].includes(s)) stages.add(s);
                      });
                    }
                    return stages.size > 0 ? stages : new Set(['EXECUTION']);
                  };
                  // Returns true if the tab belongs to the given stage.
                  const tabBelongsToStage = (tab, stageKey) =>
                    getTabStages(tab).has(stageKey);
                  const stageHasAllFilled = (stageKey) => {
                    // Determine which sub-ops belong to this stage.
                    // When filteredSubopList is populated (op-type API was called)
                    // restrict to those sub-ops. Otherwise (edit / view mode) fall
                    // back to all sub-ops that have any saved items in this stage.
                    const hasFilterList = filteredSubopList?.length > 0;
                    const tabsInStage = (sub_operations || []).filter((t) => {
                      if (!tabBelongsToStage(t, stageKey)) return false;
                      if (hasFilterList) return filteredSubopList.some((label) => subOpLabelMatches(t.label, label));
                      // Fallback: include tab if it has saved items in this stage
                      return (values?.[t.id] || []).some(
                        (item) => (item?.stage || 'EXECUTION').toUpperCase() === stageKey
                      );
                    });
                    if (tabsInStage.length === 0) return false;
                    // Stage is satisfied when at least ONE of its sub-ops has
                    // at least one item stamped with THIS stage.
                    // Must check item.stage — a tab like "Action List" can belong
                    // to both EXECUTION and POST; items added under PRE must not
                    // count towards POST or EXECUTION being green.
                    return tabsInStage.some((t) => {
                      const items = values?.[t.id] || [];
                      return items.some(
                        (item) => (item?.stage || 'EXECUTION').toUpperCase() === stageKey
                      );
                    });
                  };
                  const autoSavedStages = {};
                  stagesToFill.forEach((s) => {
                    autoSavedStages[s] = stageHasAllFilled(s);
                  });
                  const allStagesSaved = !hasStageInfo
                    || stagesToFill.every((s) => autoSavedStages[s]);
                  const pendingStages = stagesToFill.filter((s) => !autoSavedStages[s]);
                  const populatedSaveRow = stagesToFill.map((k) => ({ key: k, label: stageLabel(k) }));
                  const activeStageSaveRow = populatedSaveRow.some((p) => p.key === selectedStage)
                    ? selectedStage
                    : (populatedSaveRow[0]?.key || 'EXECUTION');
                  // Always show the stage pill(s) when at least one stage is
                  // populated — even with just one stage (e.g. Execution-only) and
                  // in view mode, it's still useful as a status indicator, not just
                  // a switcher between multiple stages.
                  if (populatedSaveRow.length === 0 && isLocked) return { stagePillsNode: null, saveButtonNode: null };
                  const stagePillsNode = populatedSaveRow.length > 0 ? (
                    <div
                      className="inline-flex p-1 rounded-full border"
                      style={{ borderColor: borderColor, minWidth: '15%', justifyContent: 'flex-start' }}
                    >
                      {populatedSaveRow.map((s) => {
                        const isActive = s.key === activeStageSaveRow;
                        const isSaved = !!autoSavedStages[s.key];
                        const savedBg = isDark ? '#14532d' : '#dcfce7';
                        const savedFg = isDark ? '#bbf7d0' : '#166534';
                        // Active (not-yet-saved) state uses the project's teal theme
                        // color instead of a generic highlight, matching the industrial
                        // teal used throughout the app (defaultColors.primary).
                        const activeBg = isDark ? 'rgba(32,68,72,0.35)' : '#f0fafa';
                        const activeFg = isDark ? '#5fb8bd' : defaultColors.primary;
                        return (
                          <button
                            type="button"
                            key={s.key}
                            onClick={() => setSelectedStage(s.key)}
                            className="px-4 py-1.5 rounded-full text-sm font-medium transition flex items-center gap-1"
                            style={{
                              backgroundColor: isActive
                                ? (isSaved ? savedBg : activeBg)
                                : (isSaved ? savedBg : 'transparent'),
                              color: isSaved ? savedFg : (isActive ? activeFg : secondaryTextColor),
                              boxShadow: isActive ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                            }}
                            title={isSaved ? `${s.label} stage has data` : `${s.label} stage — add at least one sub-operation`}
                          >
                            {isSaved && <span aria-hidden>✓</span>}
                            {s.label}
                          </button>
                        );
                      })}
                    </div>
                  ) : null;
                  // Save / Update button — edit mode only.
                  const saveButtonNode = !isLocked ? (
                    <div className="flex flex-col items-end">
                      {/* Always clickable — validation happens on submit and
                          surfaces a clear message about what's missing, instead
                          of a silently-disabled button that does nothing. */}
                      <button
                        htmlType="submit"
                        type="submit"
                        onClick={() => {
                          // Surface required-field errors immediately as a toast,
                          // so a click always gives feedback (Yup blocks onSubmit
                          // when these fail, so we message them here too).
                          const labels = {
                            operation_type_id: 'Operation Type',
                            operation_name: 'Operation Name',
                            operation_number: 'Operation Number',
                            min_duration: 'Min Duration',
                            max_duration: 'Max Duration',
                          };
                          const missing = Object.keys(errors || {})
                            .filter((k) => labels[k])
                            .map((k) => labels[k]);
                          if (missing.length > 0) {
                            toast.warning(`Please fill required field${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}.`);
                          }
                        }}
                        title={
                          allStagesSaved
                            ? ''
                            : `Add at least one sub-operation to stage${pendingStages.length > 1 ? 's' : ''}: ${pendingStages.map(stageLabel).join(', ')}`
                        }
                        className={`border-2 px-4 py-2 rounded-lg ${isDark ? 'bg-[#0F172A]' : 'bg-[#204448]'
                          } text-white
                   hover:scale-110 transition-transform duration-200`}
                      >
                        {processConfig?.operation_id
                          ? 'Update Operation'
                          : from === 'config'
                            ? 'Save Operation'
                            : from === 'temporary_operation'
                              ? 'Create Temporary Operation'
                              : from === 'standard_block' && blockData
                                ? 'Update Block'
                                : 'Save Block'}
                      </button>
                      {!allStagesSaved && (
                        <div className="text-xs mt-1" style={{ color: secondaryTextColor }}>
                          Add at least one sub-operation to stage{pendingStages.length > 1 ? 's' : ''}: <b>{pendingStages.map(stageLabel).join(', ')}</b>
                        </div>
                      )}
                    </div>
                  ) : null;
                  return { stagePillsNode, saveButtonNode };
                })();

                return (
                  <Form layout="vertical" onFinish={handleSubmit}>
                    <div
                      className="rounded-lg border overflow-hidden"
                      style={{ backgroundColor: formBgColor, borderColor: borderColor }}
                    >
                      {/* Operation Number Header */}
                      <div
                        className="flex items-center gap-3 px-4 py-2"
                        style={{
                          backgroundColor: isDark
                            ? defaultColors.palette.gray[700]
                            : defaultColors.palette.gray[100],
                          borderBottom: `1px solid ${borderColor}`,
                        }}
                      >
                        <div
                          className="flex items-center justify-center rounded-full text-white text-xs font-bold"
                          style={{
                            width: 28,
                            height: 28,
                            backgroundColor: defaultColors.primary,
                            flexShrink: 0,
                          }}
                        >
                          {values.operation_number ?? '—'}
                        </div>
                        <span className="text-sm font-semibold" style={{ color: textColor }}>
                          {(() => {
                            const fullTitle = values.operation_name
                              ? `Operation ${values.operation_number} — ${values.operation_name}`
                              : `Operation ${values.operation_number ?? ''}`;
                            const truncated =
                              fullTitle.length > OPERATION_TITLE_DISPLAY_MAX
                                ? `${fullTitle.slice(0, OPERATION_TITLE_DISPLAY_MAX)}...`
                                : fullTitle;
                            return fullTitle.length > OPERATION_TITLE_DISPLAY_MAX ? (
                              <Tooltip title={fullTitle}>
                                <span className="cursor-default">{truncated}</span>
                              </Tooltip>
                            ) : (
                              fullTitle
                            );
                          })()}
                        </span>
                        {/* Duration Required, Review & Approve, E-Sign Required, Required
                            File Uploads and Save/Update — pinned to the right end of the
                            title bar. Duration Required's static pill was replaced by the
                            functional toggle below (same treatment as Review & Approve). */}
                        <div className="flex items-center gap-4 ml-auto flex-shrink-0">
                          {durationRequiredNode}
                          {reviewApproveNode}
                          {esignRequiredNode}
                          {requiredFileUploadsNode}
                          {/* SP2 / SE1: operation-level SoP override; type-level links show greyed as inherited */}
                          {processConfig?.operation_id ? (
                            <SopLinks compact readOnly={isLocked} objectType="OPERATION" objectRef={processConfig.operation_id} />
                          ) : null}
                          {stageControls.saveButtonNode}
                        </div>
                      </div>

                      <div className="p-3">
                        <Row gutter={16}>
                  {/* Fixed 16/8 split — Min/Max/Deviation Rule and the Execution pill
                      now live in their own Row 2 below, so this row no longer needs
                      to rebalance based on Duration Required. */}
                  <Col span={16}>
                    <Row gutter={16}>
                          {from !== 'standard_block' &&
                            processConfig?.selected_standard_block && ( // hide field for standard block configuration
                              <Col span={6}>
                                <Form.Item label="Standard Block">
                                  <Select
                                    value={values?.standard_block}
                                    placeholder="Select Block"
                                    disabled={!!(processConfig?.selected_standard_block || isLocked)}
                                    size="large"
                                    onChange={(val, option) => {
                                      setFieldValue('standard_block', val || null);
                                      if (val) {
                                        setFieldValue('standard_block_label', option.label);
                                      } else {
                                        setFieldValue('standard_block_label', null);
                                      }
                                    }}
                                    showSearch
                                    allowClear
                                    optionFilterProp="children"
                                    filterOption={(input, option) =>
                                      (option?.label ?? '')
                                        .toLowerCase()
                                        .includes(input.toLowerCase())
                                    }
                                    options={standard_block_list?.map((item) => ({
                                      label: item.operation_type,
                                      value: item.operation_type_id,
                                    }))}
                                  />
                                </Form.Item>
                              </Col>
                            )}
                          <Col span={from === 'standard_block' ? 8 : 6}>
                            <Form.Item
                              label="Operation Name"
                              required
                              validateStatus={
                                touched.operation_name && errors.operation_name ? 'error' : ''
                              }
                              help={touched.operation_name && errors.operation_name}
                            >
                              <Input
                                value={values.operation_name}
                                size="large"
                                // Editable when adding via a standard block too — only view mode
                                // (read-only) keeps it disabled so the user can rename the operation.
                                disabled={isLocked}
                                onChange={(e) => setFieldValue('operation_name', e.target.value)}
                              />
                            </Form.Item>
                          </Col>
                          {values.operation_number + 1 > 1 && from === 'config' && (() => {
                            // Operation 1 has nothing before it in this
                            // process to depend on — dependant_operations_list
                            // (trimmed to strictly-earlier operations, see
                            // trimProcessesByOperationNumber above) would just
                            // be empty for it, which reads as a broken/loading
                            // dropdown rather than "not applicable here". Kept
                            // visible (not hidden) so its presence and meaning
                            // stay consistent across every operation, just
                            // disabled with an explanation.
                            const isFirstOperation = values.operation_number === 1;
                            return (
                            <Col span={6}>
                              <Form.Item label="Dependent Operations">
                                <Tooltip title={isFirstOperation ? "This is the first operation in this process — there's nothing earlier for it to depend on." : undefined}>
                                <TreeSelect
                                  showSearch
                                  multiple
                                  treeCheckable
                                  disabled={isLocked || isFirstOperation}
                                  treeCheckStrictly // parent/child independent
                                  style={{
                                    width: '100%',
                                    color: 'black',
                                  }}
                                  value={
                                    values?.dependencies?.length > 0
                                      ? values?.dependencies?.split(',').map((v) => {
                                        const base = v.replace(/_pass$|_fail$/, '');
                                        const suffix = v.endsWith('_pass') ? ' (Pass)' : v.endsWith('_fail') ? ' (Fail)' : '';
                                        // Find operation name from tree data
                                        let opName = '';
                                        dependant_operations_list?.forEach((proc) => {
                                          (proc.children || []).forEach((op) => {
                                            if (op.value === base) opName = op.opType || '';
                                          });
                                        });
                                        return { value: v, label: `Op ${base}${opName ? ' - ' + opName : ''}${suffix}` };
                                      })
                                      : []
                                  }
                                  dropdownStyle={{
                                    width: '450px',
                                    overflow: 'auto',
                                  }}
                                  placeholder="Select Dependent Operations"
                                  allowClear
                                  size="large"
                                  treeDefaultExpandAll
                                  onChange={async (selected) => {
                                    const rawValues = (
                                      selected?.map((v) => (typeof v === 'object' ? v.value : v)) || []
                                    ).filter((v) => !String(v).startsWith('process_'));

                                    // Build flat lookup
                                    const flatOps = {};
                                    dependant_operations_list.forEach((proc) => {
                                      (proc.children || []).forEach((op) => { flatOps[op.value] = op; });
                                    });

                                    // Find newly added (not in current dependencies)
                                    const prevValues = values?.dependencies?.length > 0 ? values.dependencies.split(',') : [];
                                    // Keep existing values that are still selected (strip _pass/_fail suffix for comparison)
                                    const kept = prevValues.filter((pv) => {
                                      const base = pv.replace(/_pass$|_fail$/, '');
                                      return rawValues.includes(base);
                                    });
                                    const newlyAdded = rawValues.filter((v) => !prevValues.some((pv) => pv === v || pv.startsWith(v + '_')));

                                    // For each newly added, check is_sampling and pass/fail availability
                                    for (const val of newlyAdded) {
                                      const opNode = flatOps[val];
                                      if (opNode?.is_sampling) {
                                        const hasPass = !!opNode.is_pass_dependancy;
                                        const hasFail = !!opNode.is_fail_dependancy;

                                        if (hasPass && hasFail) {
                                          // Both already taken — use normal dependency
                                          kept.push(val);
                                        } else {
                                          // Show modal with available options
                                          const choice = await new Promise((resolve) => {
                                            setSamplingModal({ open: true, opNumber: val, resolve, showPass: !hasPass, showFail: !hasFail });
                                          });
                                          if (choice === 'normal') {
                                            kept.push(val);
                                          } else if (choice) {
                                            kept.push(`${val}_${choice}`);
                                          }
                                        }
                                      } else {
                                        kept.push(val);
                                      }

                                      // Multi-machine-group dependency: prompt the user to pick
                                      // the machine group(s) for this dependent operation.
                                      if (
                                        opNode?.is_multi_machine_group &&
                                        Array.isArray(opNode?.machine_groups) &&
                                        opNode.machine_groups.length > 0
                                      ) {
                                        setMgModalValue(null);
                                        setMgModalDepType('machine');
                                        const groupIds = await new Promise((resolve) => {
                                          setMachineGroupModal({
                                            open: true,
                                            opNumber: val,
                                            options: opNode.machine_groups,
                                            resolve,
                                          });
                                        });
                                        // groupIds is an array on Confirm (machine-specific ids,
                                        // or [] for a normal whole-operation dependency) and
                                        // null on Cancel (skip — don't record anything).
                                        // REPLACE the binding for this upstream op with the new
                                        // pick (re-picking a group swaps it, not accumulates), so
                                        // edited groups don't keep the old value on the edge.
                                        if (Array.isArray(groupIds)) {
                                          setSelectedMachineGroups((prev) => ({
                                            ...prev,
                                            [val]: Array.from(new Set(groupIds)),
                                          }));
                                        }
                                      }
                                    }
                                    setFieldValue('dependencies', kept.join(','));
                                  }}
                                  treeData={dependant_operations_list}
                                  showCheckedStrategy={TreeSelect.SHOW_CHILD}
                                  maxTagCount={2}
                                />
                                </Tooltip>
                              </Form.Item>
                            </Col>
                            );
                          })()}
                          {/* Operation Number hidden — shown in card header, still sent in request via values.operation_number */}
                          <Col span={from === 'standard_block' ? 8 : 6}>
                            <Form.Item
                              required
                              label="Operation Type"
                              validateStatus={
                                touched.operation_type_id && errors.operation_type_id ? 'error' : ''
                              }
                              help={touched.operation_type_id && errors.operation_type_id}
                            >
                              <Space.Compact className="w-full">
                                <Select
                                  value={values.operation_type_id}
                                  placeholder="Select operation type"
                                  disabled={
                                    !!(processConfig?.selected_operation_type ||
                                      processConfig?.selected_standard_block ||
                                      isLocked)
                                  }
                                  onChange={(val, option) => {
                                    setFieldValue('operation_type_id', val || null);
                                    if (val) {
                                      setFieldValue('operation_type', option.label);
                                      // Auto-populate approval/esign flags from the selected operation type
                                      const selectedOpType = operation_type_list?.find(
                                        (item) => item.operation_type_id === val
                                      );
                                      if (selectedOpType) {
                                        const isCritMat = !!selectedOpType.is_critical;
                                        setFieldValue('is_esign_required', !!selectedOpType.is_esign_required);
                                        // Critical material forces supervisor approval on
                                        setFieldValue('is_supervisor_approval_required', isCritMat || !!selectedOpType.is_supervisor_approval_required);
                                        setFieldValue('is_qa_approval_required', !!selectedOpType.is_qa_approval_required);
                                        setFieldValue('is_critical', isCritMat);
                                        // Hold-time fields (Hold Min/Max) are only shown when the
                                        // selected operation type requires a hold-time step.
                                        setFieldValue('is_hold_time', !!selectedOpType.is_hold_time);
                                        if (!selectedOpType.is_hold_time) {
                                          setFieldValue('hold_min_duration', null);
                                          setFieldValue('hold_max_duration', null);
                                        }
                                      }
                                    } else {
                                      setFieldValue('operation_type', null);
                                      setFieldValue('is_esign_required', false);
                                      setFieldValue('is_supervisor_approval_required', false);
                                      setFieldValue('is_qa_approval_required', false);
                                      setFieldValue('is_critical', false);
                                      setFieldValue('is_hold_time', false);
                                      setFieldValue('hold_min_duration', null);
                                      setFieldValue('hold_max_duration', null);
                                    }

                                    //   assign sub operation list
                                    get_sub_operation_by_operation_type(
                                      success_get_subopList_byOpType,
                                      {
                                        operation_type_id: val,
                                      }
                                    );
                                  }}
                                  showSearch
                                  allowClear
                                  size="large"
                                  style={{ width: '100%' }}
                                  optionFilterProp="children"
                                  filterOption={(input, option) =>
                                    (option?.label ?? '')
                                      .toLowerCase()
                                      .includes(input.toLowerCase())
                                  }
                                  options={operation_type_list?.map((item) => ({
                                    label: item.operation_type,
                                    value: item.operation_type_id,
                                  }))}
                                />

                                {/* <Tooltip title="Add Operation Type">
                            <Button
                              size="large"
                              icon={<PlusOutlined />}
                              // onClick={() => setIsOperationTypeAdd(true)}
                            />
                          </Tooltip> */}
                              </Space.Compact>
                            </Form.Item>
                          </Col>

                          <Col span={from === 'standard_block' ? 8 : 6}>
                            <Form.Item
                              required
                              label="Operation By - Department"
                              validateStatus={
                                touched.department_id && errors.department_id ? 'error' : ''
                              }
                              help={touched.department_id && errors.department_id}
                            >
                              <Select
                                value={values.department_id}
                                placeholder="Select department"
                                onChange={(val, option) => {
                                  setFieldValue('department_id', val || '');
                                  if (val) {
                                    setFieldValue('department', option.label);
                                  } else {
                                    setFieldValue('department', null);
                                  }
                                }}
                                showSearch
                                allowClear
                                disabled={isLocked}
                                size="large"
                                style={{ width: '100%' }}
                                optionFilterProp="children"
                                filterOption={(input, option) =>
                                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={department_list?.map((item) => ({
                                  label: item.department_name,
                                  value: item.id,
                                }))}
                              />
                            </Form.Item>
                          </Col>
                    </Row>
                  </Col>
                  <Col span={8}>
                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item label={instructionLabel} style={{ marginBottom: 0 }}>
                          <Input.TextArea
                            size="large"
                            autoSize={{ minRows: 1, maxRows: 4 }}
                            value={values.instruction}
                            placeholder={instructionLabel}
                            disabled={isLocked}
                            onChange={(e) => setFieldValue('instruction', e.target.value)}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
              </Row>

              {/* Row 2 — full width, appears only when applicable, sits between the
                  always-5-fields first row above and the Execution/sub-operation
                  section below. Row 1 (Operation Name/Dependent Operations/Operation
                  Type/Operation By - Department/Instruction) never changes regardless
                  of Duration Required. */}
              {/* Row 2 — full width, ALWAYS renders (Bootstrap-style 12-col split):
                  Duration Required ON:  col-8 = Min/Max/Deviation Rule (+ Hold Min/Max),
                                          col-4 = Execution pill (centered).
                  Duration Required OFF: col-4 = Execution pill (centered), first;
                                          col-8 = blank, second.
                  Row 1 (Operation Name/Dependent Operations/Operation Type/Operation
                  By - Department/Instruction) never changes regardless of this. */}
              {(() => {
                const durationOn = durationOnHeader;
                const pillCol = (
                  <Col span={8}>
                    {/* Duration Required on: align-items end, so the pill sits level
                        with the bottom of the Min/Max/Deviation Rule input boxes
                        (not the middle of their label+input height), centered
                        horizontally. Off: left-aligned, vertically centered (its own
                        row, nothing else to line up against). */}
                    <div className={`flex h-full ${durationOn ? 'items-end justify-center' : 'items-center justify-start'}`}>
                      {stageControls.stagePillsNode}
                    </div>
                  </Col>
                );
                const fieldsCol = (
                  <Col span={16}>
                    {/* Min / Max / Deviation Rule — shown only when Duration Required
                        is on. Same size="large" + default label styling as Operation
                        Name/Operation Type/Operation By - Department. */}
                    {durationOn && (
                      <Row gutter={12}>
                        <Col span={8}>
                          <Form.Item
                            required={!isContinuousMonitoring}
                            label={
                              isContinuousMonitoring ? (
                                <span>
                                  Min{' '}
                                  <span className="text-xs text-blue-500 font-normal">
                                    (Continuous)
                                  </span>
                                </span>
                              ) : (
                                'Min (HH:MM)'
                              )
                            }
                            style={{ marginBottom: 0 }}
                            validateStatus={
                              !isContinuousMonitoring &&
                                touched.min_duration &&
                                errors.min_duration
                                ? 'error'
                                : ''
                            }
                            help={
                              !isContinuousMonitoring &&
                              touched.min_duration &&
                              errors.min_duration
                            }
                          >
                            <TimePicker
                              style={{ width: '100%' }}
                              size="large"
                              disabled={isLocked || isContinuousMonitoring}
                              format="HH:mm"
                              value={
                                isContinuousMonitoring
                                  ? dayjs('00:00', 'HH:mm')
                                  : values.min_duration
                                    ? dayjs(values.min_duration, 'HH:mm')
                                    : null
                              }
                              onChange={(time, timeString) => {
                                if (!isContinuousMonitoring) {
                                  setFieldValue('min_duration', timeString || '00:00');
                                }
                              }}
                              placeholder={isContinuousMonitoring ? '00:00' : 'Min duration'}
                            />
                          </Form.Item>
                        </Col>

                        <Col span={8}>
                          <Form.Item
                            required={!isContinuousMonitoring}
                            label={
                              isContinuousMonitoring ? (
                                <span>
                                  Max{' '}
                                  <span className="text-xs text-blue-500 font-normal">
                                    (Continuous)
                                  </span>
                                </span>
                              ) : (
                                'Max (HH:MM)'
                              )
                            }
                            style={{ marginBottom: 0 }}
                            validateStatus={
                              !isContinuousMonitoring &&
                                touched.max_duration &&
                                errors.max_duration
                                ? 'error'
                                : ''
                            }
                            help={
                              !isContinuousMonitoring &&
                              touched.max_duration &&
                              errors.max_duration
                            }
                          >
                            <TimePicker
                              style={{ width: '100%' }}
                              size="large"
                              disabled={isLocked || isContinuousMonitoring}
                              format="HH:mm"
                              value={
                                isContinuousMonitoring
                                  ? dayjs('00:00', 'HH:mm')
                                  : values.max_duration
                                    ? dayjs(values.max_duration, 'HH:mm')
                                    : null
                              }
                              onChange={(time, timeString) => {
                                if (!isContinuousMonitoring) {
                                  setFieldValue('max_duration', timeString || '00:00');
                                }
                              }}
                              placeholder={isContinuousMonitoring ? '00:00' : 'Max duration'}
                            />
                          </Form.Item>
                        </Col>

                        <Col span={8}>
                          <Form.Item label="Deviation Rule" style={{ marginBottom: 0 }}>
                            <Select
                              style={{ width: '100%' }}
                              size="large"
                              disabled={isLocked}
                              value={values.duration_deviation_rule || 'none'}
                              onChange={(val) =>
                                setFieldValue('duration_deviation_rule', val)
                              }
                              options={DEVIATION_RULE_OPTIONS}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    )}

                    {/* Hold time (min / max) — operation-level. Shown ONLY when the
                        selected operation type has Hold Time Required enabled. */}
                    {values.is_hold_time && (
                      <Row gutter={12} wrap={false} style={{ marginTop: durationOn ? 8 : 0 }}>
                        <Col span={12} style={{ minWidth: 0 }}>
                          <Form.Item label="Hold Min (HH:MM)" style={{ marginBottom: 0 }}>
                            <TimePicker
                              style={{ width: '100%' }}
                              size="large"
                              disabled={isLocked}
                              format="HH:mm"
                              value={
                                values.hold_min_duration
                                  ? dayjs(values.hold_min_duration, 'HH:mm')
                                  : null
                              }
                              onChange={(time, timeString) => {
                                setFieldValue('hold_min_duration', timeString || '00:00');
                              }}
                              placeholder="Hold min"
                            />
                          </Form.Item>
                        </Col>

                        <Col span={12} style={{ minWidth: 0 }}>
                          <Form.Item label="Hold Max (HH:MM)" style={{ marginBottom: 0 }}>
                            <TimePicker
                              style={{ width: '100%' }}
                              size="large"
                              disabled={isLocked}
                              format="HH:mm"
                              value={
                                values.hold_max_duration
                                  ? dayjs(values.hold_max_duration, 'HH:mm')
                                  : null
                              }
                              onChange={(time, timeString) => {
                                setFieldValue('hold_max_duration', timeString || '00:00');
                              }}
                              placeholder="Hold max"
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    )}
                  </Col>
                );
                return (
                  <Row gutter={16} align="stretch" style={{ marginTop: 12 }}>
                    {durationOn ? (
                      <>
                        {fieldsCol}
                        {pillCol}
                      </>
                    ) : (
                      <>
                        {pillCol}
                        <Col span={16} />
                      </>
                    )}
                  </Row>
                );
              })()}

              {from === 'standard_block' && (
              <Row gutter={16}>

                          {/* CFR 21 Part 11 Compliance Fields - Only for Standard Block */}
                            <>
                              <Col span={24} style={{ marginTop: 16, marginBottom: 12, paddingTop: 14, borderTop: `1px solid ${borderColor}` }}>
                                <div className="flex items-center gap-2">
                                  <ShieldCheck size={18} style={{ color: defaultColors.primary }} />
                                  <Title level={5} style={{ margin: 0, color: isDark ? '#e5e7eb' : defaultColors.primary }}>
                                    CFR 21 Part 11 Compliance Information
                                  </Title>
                                </div>
                              </Col>

                              <Col span={6}>
                                <Form.Item label="Compliance Level">
                                  <Select
                                    value={values.compliance_level || 'MAJOR'}
                                    onChange={(val) => setFieldValue('compliance_level', val)}
                                    disabled={isLocked}
                                    size="large"
                                    style={{ width: '100%' }}
                                  >
                                    <Select.Option value="CRITICAL">Critical</Select.Option>
                                    <Select.Option value="MAJOR">Major</Select.Option>
                                    <Select.Option value="MINOR">Minor</Select.Option>
                                    <Select.Option value="INFORMATIONAL">
                                      Informational
                                    </Select.Option>
                                  </Select>
                                </Form.Item>
                              </Col>

                              <Col span={6}>
                                <Form.Item label="Risk Level">
                                  <Select
                                    value={values.risk_level || 'MEDIUM'}
                                    onChange={(val) => setFieldValue('risk_level', val)}
                                    disabled={isLocked}
                                    size="large"
                                    style={{ width: '100%' }}
                                  >
                                    <Select.Option value="HIGH">High</Select.Option>
                                    <Select.Option value="MEDIUM">Medium</Select.Option>
                                    <Select.Option value="LOW">Low</Select.Option>
                                  </Select>
                                </Form.Item>
                              </Col>

                              <Col span={6}>
                                <Form.Item label="Minimum Signatures Required">
                                  <Select
                                    value={values.min_signature_count || 1}
                                    onChange={(val) => setFieldValue('min_signature_count', val)}
                                    disabled={isLocked}
                                    size="large"
                                    style={{ width: '100%' }}
                                  >
                                    <Select.Option value={1}>1</Select.Option>
                                    <Select.Option value={2}>2</Select.Option>
                                    <Select.Option value={3}>3</Select.Option>
                                    <Select.Option value={4}>4</Select.Option>
                                  </Select>
                                </Form.Item>
                              </Col>

                              <Col span={6}>
                                <Form.Item label="Audit Trail Retention">
                                  <Select
                                    value={values.audit_retention_years || 5}
                                    onChange={(val) => setFieldValue('audit_retention_years', val)}
                                    disabled={isLocked}
                                    size="large"
                                    style={{ width: '100%' }}
                                  >
                                    <Select.Option value={1}>1 year</Select.Option>
                                    <Select.Option value={2}>2 years</Select.Option>
                                    <Select.Option value={3}>3 years</Select.Option>
                                    <Select.Option value={5}>5 years</Select.Option>
                                    <Select.Option value={7}>7 years</Select.Option>
                                    <Select.Option value={10}>10 years</Select.Option>
                                  </Select>
                                </Form.Item>
                              </Col>

                              <Col span={4}>
                                <Form.Item label="Critical Control Point (CCP)">
                                  <Checkbox
                                    checked={values.is_critical || false}
                                    disabled={isLocked}
                                    onChange={(e) => setFieldValue('is_critical', e.target.checked)}
                                  >
                                    Yes
                                  </Checkbox>
                                </Form.Item>
                              </Col>

                              <Col span={4}>
                                <Form.Item label="GxP Relevant">
                                  <Checkbox
                                    checked={values.is_gxp_relevant !== false}
                                    disabled={isLocked}
                                    onChange={(e) =>
                                      setFieldValue('is_gxp_relevant', e.target.checked)
                                    }
                                  >
                                    Yes
                                  </Checkbox>
                                </Form.Item>
                              </Col>

                              <Col span={4}>
                                <Form.Item label="Requires QA Review">
                                  <Checkbox
                                    checked={values.requires_qa_review || false}
                                    disabled={isLocked}
                                    onChange={(e) =>
                                      setFieldValue('requires_qa_review', e.target.checked)
                                    }
                                  >
                                    Yes
                                  </Checkbox>
                                </Form.Item>
                              </Col>

                              <Col span={4}>
                                <Form.Item label="Requires Witness Signature">
                                  <Checkbox
                                    checked={values.requires_witness || false}
                                    disabled={isLocked}
                                    onChange={(e) =>
                                      setFieldValue('requires_witness', e.target.checked)
                                    }
                                  >
                                    Yes
                                  </Checkbox>
                                </Form.Item>
                              </Col>

                              <Col span={4}>
                                <Form.Item label="Requires Validation">
                                  <Checkbox
                                    checked={values.requires_validation || false}
                                    disabled={isLocked}
                                    onChange={(e) =>
                                      setFieldValue('requires_validation', e.target.checked)
                                    }
                                  >
                                    Yes
                                  </Checkbox>
                                </Form.Item>
                              </Col>

                              <Col span={4}>
                                <Form.Item label="Requires Deviation Justification">
                                  <Checkbox
                                    checked={values.requires_deviation_justification !== false}
                                    disabled={isLocked}
                                    onChange={(e) =>
                                      setFieldValue(
                                        'requires_deviation_justification',
                                        e.target.checked
                                      )
                                    }
                                  >
                                    Yes
                                  </Checkbox>
                                </Form.Item>
                              </Col>
                            </>
                        </Row>
                      )}
                      </div>
                      {/* end p-3 */}
                    </div>

                    <div className="mt-4">
                      {(() => {
                        // Tabs allowed for this operation type (filteredSubopList = list
                        // of sub_operation labels from operation-type config).
                        // In view mode filteredSubopList may be empty (op-type API not called).
                        // Fall back to showing only sub-ops that have saved items.
                        // Use normalized label matching so "Api Calculations" matches
                        // "API Calculation" from the DB, preventing POST tabs from
                        // being silently dropped due to capitalisation/plural differences.
                        const subOpInFilteredList = (op) =>
                          (filteredSubopList || []).some((label) => subOpLabelMatches(op?.label, label));
                        const allowedTabs =
                          (sub_operations?.filter(subOpInFilteredList)?.length > 0
                            ? sub_operations.filter(subOpInFilteredList)
                            : isLocked
                              ? sub_operations.filter((op) => (values?.[op.id] || []).length > 0) || sub_operations?.slice(0, 8)
                              : sub_operations?.slice(0, 8)) || [];
                        // Group by stage from subopStageMap; legacy/unknown -> EXECUTION.
                        const stages = [
                          { key: 'PRE', label: 'Pre' },
                          { key: 'EXECUTION', label: 'Execution' },
                          { key: 'POST', label: 'Post' },
                        ];
                        // Re-declare getTabStages here (same logic as in the stage-pills
                        // IIFE above) so this second IIFE can also use it.
                        const getTabStagesLocal = (tab) => {
                          const fromMap = subopStageMap?.[normalizeSubOpLabel(tab.label)];
                          if (fromMap instanceof Set && fromMap.size > 0) return fromMap;
                          if (typeof fromMap === 'string') return new Set([fromMap.toUpperCase()]);
                          const stagesFound = new Set();
                          const savedItems = values?.[tab.id];
                          if (Array.isArray(savedItems)) {
                            savedItems.forEach((item) => {
                              const s = (item?.stage || '').toString().toUpperCase();
                              if (['PRE', 'EXECUTION', 'POST'].includes(s)) stagesFound.add(s);
                            });
                          }
                          return stagesFound.size > 0 ? stagesFound : new Set(['EXECUTION']);
                        };
                        const stageTabs = { PRE: [], EXECUTION: [], POST: [] };
                        allowedTabs.forEach((tab) => {
                          // A tab can belong to multiple stages (e.g. "Action List" in
                          // EXECUTION and POST). Push it into every stage it belongs to.
                          const tabStages = getTabStagesLocal(tab);
                          tabStages.forEach((st) => {
                            if (stageTabs[st]) stageTabs[st].push(tab);
                            else stageTabs.EXECUTION.push(tab);
                          });
                        });
                        // renderPill accepts stageKey so the count badge shows only
                        // items stamped with that stage (not all items across all stages).
                        const renderPill = (tab, stageKey) => {
                          const Icon = tab.icon;
                          // Count only items belonging to this stage.
                          const allItems = values[tab?.id] || initialValues?.[tab?.id] || [];
                          const stageItems = stageKey
                            ? allItems.filter(
                              (item) => (item?.stage || 'EXECUTION').toUpperCase() === stageKey
                            )
                            : allItems; // single-stage fallback: show all
                          const stageCount = stageItems.length;
                          return (
                            <button
                              type="button"
                              key={tab.id}
                              onClick={() => setActiveTab(tab.id)}
                              className="flex-1 flex items-center justify-center gap-1 px-4 py-1 rounded-full text-sm font-medium transition whitespace-nowrap"
                              style={{
                                backgroundColor:
                                  activeTab === tab?.id ? activeTabBgColor : 'transparent',
                                color: activeTab === tab?.id ? textColor : secondaryTextColor,
                                boxShadow:
                                  activeTab === tab?.id ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                              }}
                            >
                              <Icon size={16} />
                              {subOpAlias(tab.label)}
                              {Array?.isArray(initialValues?.[tab?.id]) && (
                                <span
                                  className={`ml-1 px-2 py-0.5 text-xs font-semibold rounded-full cursor-default ${stageCount > 0
                                      ? isDark
                                        ? 'bg-[#0F172A] text-white'
                                        : 'bg-[#204448] text-white'
                                      : 'bg-gray-300 text-blue-700'
                                    }`}
                                >
                                  {stageCount}
                                </span>
                              )}
                            </button>
                          );
                        };
                        // Stages with no tabs are hidden.
                        // Always use the segmented-control path so the correct stage
                        // button(s) show — even when only one stage is configured.
                        const populated = stages.filter((s) => stageTabs[s.key].length > 0);
                        if (populated.length === 0) {
                          return (
                            <div className="w-full overflow-x-auto">
                              <div
                                className="flex gap-2 p-1 rounded-full border w-full"
                                style={{ backgroundColor: tabBgColor, borderColor: borderColor }}
                              >
                                {allowedTabs.map((tab) => renderPill(tab, null))}
                              </div>
                            </div>
                          );
                        }
                        // Multi-stage operation type: segmented control on top, only the
                        // active stage's sub-op pill bar is shown. Each stage has its own
                        // "Save Stage" button to mark it filled (green check). The final
                        // operation Save is disabled until every populated stage is saved.
                        const activeStage = populated.some((p) => p.key === selectedStage)
                          ? selectedStage
                          : populated[0].key;
                        // If the segmented control's active stage is no longer present
                        // in populated (e.g. op-type changed), snap to first populated.
                        if (activeStage !== selectedStage) {
                          setTimeout(() => setSelectedStage(activeStage), 0);
                        }
                        // Also keep the activeTab inside one of the active stage's pills.
                        const activeStagePillIds = stageTabs[activeStage].map((t) => t.id);
                        if (activeStagePillIds.length && !activeStagePillIds.includes(activeTab)) {
                          setTimeout(() => setActiveTab(activeStagePillIds[0]), 0);
                        }
                        // Pill bar for the active stage only. The segmented control
                        // (Pre / Execution / Post) + per-stage Save button now sit on
                        // the left of the Save Operation row below.
                        return (
                          <div className="w-full overflow-x-auto">
                            <div
                              className="flex gap-2 p-1 rounded-full border w-full"
                              style={{ backgroundColor: tabBgColor, borderColor: borderColor }}
                            >
                              {stageTabs[activeStage].map((tab) => renderPill(tab, activeStage))}
                            </div>
                          </div>
                        );
                      })()}
                      <div
                        className="mt-4 p-6 border rounded-md shadow-sm h-[43vh] flex-shrink-0 flex flex-col"
                        style={{ backgroundColor: cardBgColor, borderColor: borderColor }}
                      >
                        {/* Title and Action Icons Row */}
                        {!isLocked && (() => {
                          // Machine Setup normally allows a single entry; but when the
                          // operation-type rule has "Multi Select Machine Per Operation"
                          // enabled, allow adding more than one machine setup entry.
                          const machineMultiSelectOn = (opTypeRules?.machines || []).some(
                            (r) => r?.multi_select_machine_per_operation === true
                          );
                          // For multi-stage tabs, only count items in the current stage
                          const activeTabLabelForAdd = (sub_operations.find((s) => s.id === activeTab) || {}).label || activeTab;
                          const fromMapForAdd = subopStageMap?.[normalizeSubOpLabel(activeTabLabelForAdd)];
                          const tabStagesForAdd = fromMapForAdd instanceof Set ? fromMapForAdd
                            : typeof fromMapForAdd === 'string' ? new Set([fromMapForAdd.toUpperCase()])
                              : new Set(['EXECUTION']);
                          const isMultiStageForAdd = tabStagesForAdd.size > 1;
                          const stageItemCount = isMultiStageForAdd
                            ? (values[activeTab] || []).filter((item) => (item?.stage || 'EXECUTION').toUpperCase() === selectedStage).length
                            : (values[activeTab]?.length || 0);
                          const canAddMore =
                            stageItemCount > 0 &&
                            (activeTab !== 'machines' || machineMultiSelectOn);
                          return (
                            <div className="flex items-center justify-end mb-4">
                              {canAddMore && (
                                <button
                                  type="button"
                                  className={`flex items-center gap-2 px-3 py-1.5 border border-dashed text-sm font-medium rounded-lg transition ${requiresMachineSetupBeforeAdd
                                      ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                                      : 'border-blue-600 text-blue-600 hover:bg-blue-50 hover:border-blue-700 hover:text-blue-700'
                                    }`}
                                  disabled={requiresMachineSetupBeforeAdd}
                                  title={
                                    requiresMachineSetupBeforeAdd
                                      ? 'Setup machines first in the Machine Setup tab'
                                      : undefined
                                  }
                                  onClick={() => openModal()}
                                >
                                  <Plus className="w-4 h-4" />
                                  {`Add ${subOpAlias(visibleSubOps?.find((op) => op.id === activeTab)?.label)}`}
                                </button>
                              )}
                            </div>
                          );
                        })()}
                        {/* Rule-locked indicator */}
                        {opTypeRules[activeTab]?.length > 0 && !isLocked && (
                          <div className="flex flex-wrap items-center gap-2 mb-3">
                            <div
                              className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium w-fit"
                              style={{ backgroundColor: '#fef9c3', color: '#92400e' }}
                            >
                              <Network className="w-3.5 h-3.5" />
                              {opTypeRules[activeTab].length} item
                              {opTypeRules[activeTab].length > 1 ? 's' : ''} assigned from operation
                              type rule — locked
                            </div>
                            {activeTab === 'machines' &&
                              (opTypeRules?.machines || []).some(
                                (r) => r?.multi_select_machine_per_operation === true
                              ) && (
                                <div
                                  className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium w-fit"
                                  style={{ backgroundColor: '#dcfce7', color: '#166534' }}
                                >
                                  <Cog className="w-3.5 h-3.5" />
                                  Multi Select Machine Per Operation — Enabled
                                </div>
                              )}
                          </div>
                        )}
                        {requiresMachineSetupBeforeAdd && !isLocked && (
                          <div
                            className="mb-3 rounded-md border border-dashed px-3 py-2 text-sm"
                            style={{
                              backgroundColor: '#fef2f2',
                              borderColor: '#fca5a5',
                              color: '#991b1b',
                            }}
                          >
                            Setup machines first in the Machine Setup tab before adding{' '}
                            {activeTab === 'ingredients'
                              ? subOpAlias('Ingredients').toLowerCase()
                              : `${subOpAlias('Monitoring').toLowerCase()} parameters`}.
                          </div>
                        )}
                        {(() => {
                          // When a sub-op belongs to multiple stages (e.g. Checklist in
                          // PRE and POST), values[activeTab] holds items for ALL stages.
                          // Only show items stamped with the currently selected stage so
                          // PRE items don't bleed into POST view and vice versa.
                          // For single-stage sub-ops (tab belongs to only one stage) show all.
                          const activeTabLabel = (sub_operations.find((s) => s.id === activeTab) || {}).label || activeTab;
                          const fromMapForActive = subopStageMap?.[normalizeSubOpLabel(activeTabLabel)];
                          const tabStagesForActive = fromMapForActive instanceof Set
                            ? fromMapForActive
                            : typeof fromMapForActive === 'string'
                              ? new Set([fromMapForActive.toUpperCase()])
                              : new Set(['EXECUTION']);
                          const isMultiStageTab = tabStagesForActive.size > 1;
                          const allTabItems = values[activeTab] || [];
                          // Build list of { item, originalIndex } filtered by stage
                          const visibleItems = allTabItems
                            .map((item, idx) => ({ item, idx }))
                            .filter(({ item }) =>
                              isMultiStageTab
                                ? (item?.stage || 'EXECUTION').toUpperCase() === selectedStage
                                : true
                            );
                          if (visibleItems.length > 0) return (
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 overflow-auto">
                              {visibleItems.map(({ item, idx }) => (
                                <div
                                  key={idx}
                                  className="relative"
                                  style={
                                    !processApprovalStatus && !fromView && !item._from_rule
                                      ? { pointerEvents: 'none', opacity: 0.55 }
                                      : {}
                                  }
                                >
                                  {/* Per-item file requirement badge in card header */}
                                  {(item.file_config || []).length > 0 && (
                                    <div
                                      className="absolute flex items-center gap-1 rounded-full z-10 pointer-events-none"
                                      style={{
                                        top: 10,
                                        right: 76,
                                        background: 'linear-gradient(135deg,#6366f1 0%,#3b82f6 100%)',
                                        color: '#ffffff',
                                        boxShadow: '0 2px 6px rgba(99,102,241,0.45)',
                                        fontSize: 11,
                                        fontWeight: 700,
                                        padding: '2px 8px',
                                      }}
                                    >
                                      <Paperclip size={10} />
                                      <span>{item.file_config.length}</span>
                                    </div>
                                  )}
                                  <ActiveViewComponent
                                    item={item}
                                    fromView={fromView || item._from_rule === true}
                                    operation_duration={operation_duration_in_seconds}
                                    onEdit={() => editFunction(idx)}
                                    onDelete={() => deleteFunction(idx)}
                                    machineTypeWithGroups={machineTypeWithGroups}
                                  />
                                </div>
                              ))}
                            </div>
                          );
                          return (
                            <div
                              className="flex w-full h-full items-center justify-center h-45"
                              style={{ color: secondaryTextColor }}
                            >
                              {isLocked ? ( // locked (view mode or approval pending)
                                <div className="w-full h-full flex flex-col justify-center items-center text-center">
                                  <Package
                                    className="w-10 h-10 mx-auto mb-3"
                                    style={{ color: secondaryTextColor }}
                                  />
                                  <p className="mb-2" style={{ color: secondaryTextColor }}>
                                    No items Added
                                  </p>
                                </div>
                              ) : (
                                <div className="text-center">
                                  <Package
                                    className="w-10 h-10 mx-auto mb-3"
                                    style={{ color: secondaryTextColor }}
                                  />
                                  <p className="mb-2" style={{ color: secondaryTextColor }}>
                                    No items added yet
                                  </p>
                                  <p className="text-sm mb-4" style={{ color: secondaryTextColor }}>
                                    Click the button below to start adding items.
                                  </p>
                                  <button
                                    type="button"
                                    className={`border-2 border-dashed px-4 py-2 rounded-lg transition ${requiresMachineSetupBeforeAdd
                                        ? 'border-gray-300 text-gray-400 cursor-not-allowed'
                                        : 'border-blue-400 text-blue-500 hover:bg-blue-50'
                                      }`}
                                    disabled={requiresMachineSetupBeforeAdd}
                                    title={
                                      requiresMachineSetupBeforeAdd
                                        ? 'Setup machines first in the Machine Setup tab'
                                        : undefined
                                    }
                                    onClick={() => {
                                      openModal();
                                    }}
                                  >
                                    {`+ Add ${subOpAlias(visibleSubOps?.find((op) => op.id === activeTab)?.label)
                                      }`}
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>

                    <PageModal
                      open={isModalOpen}
                      icon={editIndex !== null ? <Pencil size={22} /> : <Plus size={22} />}
                      title={`${editIndex !== null ? 'Edit' : 'Add'} ${subOpAlias(sub_operations.find((i) => i.id === activeTab)?.label)}`}
                      destroyOnHidden
                      onCancel={() => {
                        // Discard the modal's live preview: snap the counter back to the last
                        // committed value (no change on cancel).
                        setLiveConfigured(committedConfigured);
                        setLiveMaster(committedMaster);
                        setIsModalOpen(false);
                        setEditIndex(null);
                        setItemFileConfig([]);
                      }}
                      width={1200}
                    >
                      <ActiveComponent
                        editIndex={editIndex}
                        values={modal_values}
                        onSubmit={modal_submit}
                        fromView={fromView} // for disabling fields in view mode
                        lockedRuleFields={editIndex === null ? ruleDefaults : undefined}
                        operation_is_critical={!!values.is_critical}
                        operation_duration={operation_duration_in_seconds} // Pass duration in seconds
                        process_id={processConfig?.process_id || undefined}
                        configured_machines={
                          // Flatten selected machine ids from machine setup entries.
                          (values.machines || []).flatMap((m) =>
                            Array.isArray(m.machine_ids) && m.machine_ids.length
                              ? m.machine_ids
                              : (m.machine_id ? [m.machine_id] : [])
                          )
                        }
                        machine_setup_entries={values.machines || []}
                        recipe_id={recipe_id}
                        process_code={processData?.process_code || ''}
                        // get_process_data_for_process_id returns resolved area NAMES
                        // under "areas" (plural) — not "area".
                        process_areas={processData?.areas || []}
                        operation_type_code={
                          (operation_type_list || []).find(
                            (t) => String(t.operation_type_id) === String(values.operation_type_id)
                          )?.code || ''
                        }
                        one_machine_rule_enabled={(() => {
                          return (filteredSubopList || []).some(
                            (s) => String(s).toLowerCase().replace(/[\s-]+/g, '_') === 'machine_setup'
                          );
                        })()}
                        machine_multi_select_enabled={(opTypeRules?.machines || []).some(
                          (r) => r.multi_select_machine_per_operation === true
                        )}
                        machineRule={(opTypeRules?.machines || [])[0] || null}
                        current_ingredients={
                          activeTab === 'ingredients'
                            ? (values.ingredients || []).map((it, idx) => ({
                              ingredient_id:
                                it.ingredient_id ||
                                it.work_in_progress_material_name ||
                                `idx-${idx}`,
                              ingredient:
                                it.ingredient ||
                                it.work_in_progress_material_name ||
                                `${subOpAlias('Ingredients')} ${idx + 1}`,
                              ingredient_code: it.ingredient_code || null,
                              // Quantity + row index so the form can sum this op's UI
                              // ingredients and exclude the row currently being edited.
                              quantity: it.quantity,
                              _rowIndex: idx,
                              // Pass machine info for carry-over auto-fill
                              from_machine_id: it.from_machine_id || null,
                              from_machine_category_id: it.from_machine_category_id || null,
                              from_machine_group_id: it.from_machine_group_id || null,
                              from_machine_name: it.from_machine_name || null,
                              to_machine_id: it.to_machine_id || null,
                              to_machine_category_id: it.to_machine_category_id || null,
                              to_machine_group_id: it.to_machine_group_id || null,
                              to_machine_name: it.to_machine_name || null,
                            }))
                            : []
                        }
                        previous_operations={(() => {
                          const rawDeps =
                            typeof values.dependencies === 'string'
                              ? values.dependencies.split(',').filter(Boolean)
                              : Array.isArray(values.dependencies)
                                ? values.dependencies
                                : [];
                          const depIds = rawDeps.map((d) =>
                            String(d).replace(/_pass$|_fail$/, '')
                          );
                          if (!depIds.length) return [];
                          return (operation_list || [])
                            .filter((op) =>
                              depIds.some(
                                (d) =>
                                  String(d) === String(op.operationId) ||
                                  String(d) === String(op.operation_id) ||
                                  String(d) === String(op.operation_number)
                              )
                            )
                            .map((op) => ({
                              operation_number: op.operation_number ?? op.operationId ?? op.operation_id,
                              operation_name:
                                op.operation_name || op.operation_type || '',
                            }));
                        })()}
                        operation_id={processConfig?.operation_id ?? null}
                        operation_ingredients={values.ingredients || []}
                        operation_monitoring={values.monitoring || []}
                        operation_action_steps={(values.action_list || []).map((s, idx) => ({
                          id: s.action_id ?? idx,
                          name: s.name ?? s.fields?.[0]?.name ?? `Step ${idx + 1}`,
                        }))}
                        operation_max_duration={values.is_duration_required ? values.max_duration : null}
                        {...modalProps}
                      />
                      {/* Required file uploads — button opens config popup */}
                      <div style={{
                        borderTop: '1px solid #e5e7eb',
                        padding: '10px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        background: '#f8faff',
                      }}>
                        <FileText size={14} style={{ color: '#6366f1', flexShrink: 0 }} />
                        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#374151' }}>
                          Required File Uploads
                        </span>
                        {itemFileConfig.length > 0 && (
                          <Tag style={{ fontSize: 11, margin: 0, background: '#eff0fe', borderColor: '#a5b4fc', color: '#4f46e5' }}>
                            {itemFileConfig.length} requirement{itemFileConfig.length !== 1 ? 's' : ''}
                          </Tag>
                        )}
                        <button
                          type="button"
                          onClick={() => setItemFileConfigModalOpen(true)}
                          style={{
                            marginLeft: 'auto',
                            fontSize: 12,
                            fontWeight: 600,
                            color: '#6366f1',
                            background: '#eff0fe',
                            border: '1px solid #a5b4fc',
                            borderRadius: 20,
                            padding: '4px 14px',
                            cursor: 'pointer',
                          }}
                        >
                          {isLocked
                            ? 'View Files'
                            : itemFileConfig.length > 0
                              ? 'Edit Requirements'
                              : 'Add Required Files'}
                        </button>
                      </div>
                    </PageModal>

                    {/* Operation-level File Attachment Config Modal */}
                    <FileAttachmentConfigModal
                      open={fileConfigModal.open}
                      onClose={() => setFileConfigModal({ open: false, target: null })}
                      title={`Required Files — ${values.operation_name ? `Operation ${values.operation_number}: ${values.operation_name}` : `Operation ${values.operation_number ?? ''}`}`}
                      initialData={values.operation_file_config || []}
                      readOnly={isLocked}
                      onSave={(savedRows) => setFieldValue('operation_file_config', savedRows)}
                    />

                    {/* Per-item File Attachment Config Modal */}
                    <FileAttachmentConfigModal
                      open={itemFileConfigModalOpen}
                      onClose={() => setItemFileConfigModalOpen(false)}
                      title={`Required Files — ${subOpAlias(sub_operations.find((s) => s.id === activeTab)?.label) || activeTab} Item`}
                      initialData={itemFileConfig}
                      readOnly={isLocked}
                      zIndex={1600}
                      onSave={(savedRows) => {
                        setItemFileConfig(savedRows);
                        setItemFileConfigModalOpen(false);
                      }}
                    />

                  </Form>
                );
              }}
            </Formik>
          </div>
        ) : (
          <div
            className="flex justify-center items-center h-[50vh] sm:h-[60vh] md:h-[70vh] lg:h-[75vh] w-full py-10 border"
            style={{ borderColor: borderColor }}
          >
            <div
              className="flex flex-col items-center text-center"
              style={{ color: secondaryTextColor }}
            >
              {/* Icon */}
              <div className="mb-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-20 w-20"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                  style={{ color: secondaryTextColor }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 8v4m0 4h.01M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9 9 4.03 9 9z"
                  />
                </svg>
              </div>

              {/* Text */}
              <p className="text-[16px] font-medium" style={{ color: textColor }}>
                No Operation Selected
              </p>
              <p className="text-[15px] mb-4" style={{ color: secondaryTextColor }}>
                Please select any operation to view its details.
              </p>
            </div>
          </div>
        )
      ) : (
        <div
          className="flex justify-center items-center h-[50vh] sm:h-[60vh] md:h-[70vh] lg:h-[75vh] w-full py-10"
        >
          <div
            className="flex flex-col items-center text-center"
            style={{ color: secondaryTextColor }}
          >
            {/* Icon */}
            <div className="mb-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-20 w-20"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.5}
                style={{ color: secondaryTextColor }}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 7v4a1 1 0 001 1h3v-2H4V7h16v3h-3v2h3a1 1 0 001-1V7a2 2 0 00-2-2H5a2 2 0 00-2 2zm3 8v2h12v-2H6z"
                />
              </svg>
            </div>

            {/* Text */}
            <p
              className="text-[16px]"
              style={{ color: textColor }}
            >{`Add operation for ${headerContent?.Process}`}</p>
            <p className="text-[16px] mb-4" style={{ color: secondaryTextColor }}>
              Click the button below to start adding operations.
            </p>

            {/* Button */}
            <button
              onClick={() => {
                if (!isLocked) set_is_add_middle_modal(!is_add_middle_modal);
              }}
              disabled={isLocked}
              className={`px-5 py-2 border-2 border-dashed rounded-lg transition ${isLocked
                  ? 'border-gray-300 text-gray-400 cursor-not-allowed opacity-50'
                  : 'border-blue-400 text-blue-600 hover:bg-blue-50'
                }`}
            >
              + Add Operation
            </button>
          </div>
        </div>
      )}
      {/* Sampling Pass/Fail Modal */}
      <PageModal
        open={samplingModal.open}
        icon={<FlaskConical size={22} />}
        title="Sampling Dependency Type"
        closable={false}
        width={440}
      >
        <p style={{ marginBottom: 16, color: '#555' }}>
          Operation <strong>#{samplingModal.opNumber}</strong> has sampling. Select dependency type:
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => { samplingModal.resolve?.('normal'); setSamplingModal({ open: false, opNumber: null, resolve: null }); }}
            style={{ padding: '6px 24px', borderRadius: 6, border: 'none', background: '#204448', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
          >
            Normal
          </button>
          {samplingModal.showPass && (
            <button
              onClick={() => { samplingModal.resolve?.('pass'); setSamplingModal({ open: false, opNumber: null, resolve: null }); }}
              style={{ padding: '6px 24px', borderRadius: 6, border: '2px solid #204448', background: '#fff', color: '#204448', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
            >
              Pass
            </button>
          )}
          {samplingModal.showFail && (
            <button
              onClick={() => { samplingModal.resolve?.('fail'); setSamplingModal({ open: false, opNumber: null, resolve: null }); }}
              style={{ padding: '6px 24px', borderRadius: 6, border: '2px solid #204448', background: '#fff', color: '#204448', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}
            >
              Fail
            </button>
          )}
          <button
            onClick={() => { samplingModal.resolve?.(null); setSamplingModal({ open: false, opNumber: null, resolve: null }); }}
            style={{ padding: '6px 24px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#6b7280', fontWeight: 500, cursor: 'pointer', fontSize: 14 }}
          >
            Cancel
          </button>
        </div>
      </PageModal>

      {/* Machine-group selection for multi-machine-group dependent operations */}
      <PageModal
        open={machineGroupModal.open}
        icon={<Cog size={22} />}
        title="Select Dependency Type"
        description={`Operation #${machineGroupModal.opNumber} uses multiple machine groups`}
        closable={false}
        width={520}
        bodyStyle={{ padding: 0 }}
      >
        {/* Body */}
        <div style={{ padding: '20px 24px 8px' }}>
          {[
            {
              key: 'machine',
              title: 'Machine dependent',
              desc: 'Depend on this operation only for a specific machine group.',
            },
            {
              key: 'normal',
              title: 'Normal operation dependent',
              desc: 'Depend on the whole operation, regardless of machine group.',
            },
          ].map((opt) => {
            const active = mgModalDepType === opt.key;
            return (
              <div
                key={opt.key}
                role="button"
                onClick={() => {
                  setMgModalDepType(opt.key);
                  if (opt.key !== 'machine') setMgModalValue(null);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  border: `1.5px solid ${active ? '#204448' : '#e5e7eb'}`,
                  background: active ? '#f0f7f6' : '#fff',
                  borderRadius: 8,
                  padding: '12px 14px',
                  marginBottom: 10,
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                }}
              >
                <span
                  style={{
                    flexShrink: 0,
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    border: `2px solid ${active ? '#204448' : '#cbd5e1'}`,
                    background: '#fff',
                    marginTop: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {active && (
                    <span
                      style={{
                        width: 9,
                        height: 9,
                        borderRadius: '50%',
                        background: '#204448',
                      }}
                    />
                  )}
                </span>
                <span>
                  <span
                    style={{
                      display: 'block',
                      fontWeight: 600,
                      fontSize: 14,
                      color: active ? '#204448' : '#374151',
                    }}
                  >
                    {opt.title}
                  </span>
                  <span style={{ display: 'block', fontSize: 12.5, color: '#6b7280', marginTop: 2 }}>
                    {opt.desc}
                  </span>
                </span>
              </div>
            );
          })}

          {mgModalDepType === 'machine' && (
            <div style={{ marginTop: 6, marginBottom: 4 }}>
              <label
                style={{
                  display: 'block',
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: 6,
                }}
              >
                Machine group
              </label>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder="Select machine group"
                value={mgModalValue || undefined}
                onChange={(v) => setMgModalValue(v ?? null)}
                options={(() => {
                  // Machine group ids already bound to this upstream op (from prior
                  // saved dependencies + earlier picks this session). Their options
                  // are shown disabled so the same group can't be chosen twice.
                  const usedIds = new Set(
                    selectedMachineGroups[String(machineGroupModal.opNumber)] || []
                  );
                  return (machineGroupModal.options || []).map((opt, i) => {
                    const ids = opt.machine_group_ids || [];
                    const alreadyUsed = ids.length > 0 && ids.every((id) => usedIds.has(id));
                    return {
                      label: alreadyUsed
                        ? `${opt.machine_group_names} (already selected)`
                        : opt.machine_group_names,
                      // value is a JSON string of the ids (AntD values must be primitive)
                      value: JSON.stringify(ids),
                      key: i,
                      disabled: alreadyUsed,
                    };
                  });
                })()}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            gap: 10,
            justifyContent: 'flex-end',
            padding: '14px 24px 20px',
          }}
        >
          <button
            onClick={() => {
              machineGroupModal.resolve?.(null);
              setMachineGroupModal({ open: false, opNumber: null, options: [], resolve: null });
              setMgModalValue(null);
              setMgModalDepType('machine');
            }}
            style={{ padding: '7px 22px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', color: '#6b7280', fontWeight: 500, cursor: 'pointer', fontSize: 14 }}
          >
            Cancel
          </button>
          <button
            disabled={mgModalDepType === 'machine' && !mgModalValue}
            onClick={() => {
              // 'normal' -> whole-operation dependency: send selected_machine_group: [].
              // 'machine' -> the single selected option holds a JSON-encoded id array.
              const parseOne = (s) => {
                try { return (JSON.parse(s) || []).filter((v) => v != null); }
                catch (_e) { return []; }
              };
              const ids =
                mgModalDepType === 'normal'
                  ? []
                  : mgModalValue
                    ? parseOne(mgModalValue)
                    : [];
              machineGroupModal.resolve?.(ids);
              setMachineGroupModal({ open: false, opNumber: null, options: [], resolve: null });
              setMgModalValue(null);
              setMgModalDepType('machine');
            }}
            style={{
              padding: '7px 26px',
              borderRadius: 6,
              border: 'none',
              background: mgModalDepType === 'machine' && !mgModalValue ? '#9bb5b3' : '#204448',
              color: '#fff',
              fontWeight: 600,
              cursor: mgModalDepType === 'machine' && !mgModalValue ? 'not-allowed' : 'pointer',
              fontSize: 14,
            }}
          >
            Confirm
          </button>
        </div>
      </PageModal>
    </div>
  );
};

export default OperationForm;
